/**
 * Elite Batch 10 Part 3 — Workflow Templates 888–907 (Marketing)
 * Before/after video, bid adjustment, birthday capture, brand monitoring,
 * Braze integrations, browser push, budget pacing, bulk SMS, CRM-to-ads sync.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function brazePostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://rest.iad-01.braze.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {braze_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function brazeGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://rest.iad-01.braze.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {braze_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 10 PART 3 — TEMPLATES 888–907 (Marketing)
// =============================================================================

export const ELITE_BATCH10_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 888. BEFORE/AFTER TRANSFORMATION VIDEO (9 nodes)
  // ===================================================================

  "Before/After Transformation Video": {
    summary:
      "Automates creation and publishing of before/after transformation videos. A content request webhook triggers product and testimonial data fetch, AI generates a video script, voiceover is generated via ElevenLabs, video is rendered via Shotstack, AI reviews quality, a condition gates on approval, a human approval gate ensures sign-off, and the final video is published via Buffer.",
    nodes: [
      trigger("Webhook: Transformation video content request", "webhook_app", {
        source: "CMS / Internal Portal",
        event: "content.video.requested",
        howItStarts:
          "A content team member requests a before/after transformation video by submitting product details, customer testimonials, and asset URLs",
        setup:
          "Configure your CMS or internal portal to POST content requests to this workflow URL. Include product ID, customer testimonial references, and before/after image URLs.",
        inputFields: [],
      }),

      // 2. Extract request + fetch product/testimonial data
      aiNode(
        "2",
        "Extract request & fetch product/testimonial data",
        280,
        "You are a content data extraction assistant. Extract the product ID, customer name, before image URL, after image URL, testimonial text, and product name from the webhook payload regardless of format. Return JSON.",
        "Extract video request details from: {webhook_payload}",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "before_image_url", as: "before_image_url", type: "string" },
          { field: "after_image_url", as: "after_image_url", type: "string" },
          { field: "testimonial_text", as: "testimonial_text", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
        ],
      ),

      // 3. AI script generation
      aiNode(
        "3",
        "AI: Generate video script",
        520,
        "You are a video scriptwriter specializing in before/after transformation content. Write a compelling 60-second script with intro hook, before state description, transformation journey, after reveal, testimonial integration, and CTA. Return JSON with script_text, voiceover_text (clean narration only), scene_descriptions (array), and estimated_duration_seconds.",
        "Product: {product_name}. Customer: {customer_name}. Testimonial: {testimonial_text}. Before image: {before_image_url}. After image: {after_image_url}.",
        [
          { field: "script_text", as: "script_text", type: "string" },
          { field: "voiceover_text", as: "voiceover_text", type: "string" },
          {
            field: "scene_descriptions",
            as: "scene_descriptions",
            type: "string",
          },
          {
            field: "estimated_duration_seconds",
            as: "estimated_duration_seconds",
            type: "number",
          },
        ],
      ),

      // 4. Voiceover generation (ElevenLabs)
      n("4", "hitApi", "Generate voiceover (ElevenLabs)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.elevenlabs.io/v1/text-to-speech/{elevenlabs_voice_id}",
          headers: [
            { key: "xi-api-key", value: "{elevenlabs_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "text", value: "{voiceover_text}" },
            { key: "model_id", value: "eleven_monolingual_v1" },
            {
              key: "voice_settings",
              value: '{"stability":0.75,"similarity_boost":0.8}',
            },
          ],
          variables: [
            {
              name: "audio_url",
              customName: "voiceover_audio_url",
              type: "string",
            },
            {
              name: "duration_seconds",
              customName: "voiceover_duration",
              type: "number",
            },
          ],
        },
      }),

      // 5. Video rendering (Shotstack)
      n("5", "hitApi", "Render transformation video (Shotstack)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.shotstack.io/v1/render",
          headers: [
            { key: "x-api-key", value: "{shotstack_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "timeline",
              value:
                '{"tracks":[{"clips":[{"asset":{"type":"image","src":"{before_image_url}"},"start":0,"length":5},{"asset":{"type":"image","src":"{after_image_url}"},"start":5,"length":5},{"asset":{"type":"audio","src":"{voiceover_audio_url}"},"start":0,"length":{voiceover_duration}}]}]}',
            },
            { key: "output", value: '{"format":"mp4","resolution":"hd"}' },
          ],
          variables: [
            { name: "response.id", customName: "render_id", type: "string" },
            {
              name: "response.url",
              customName: "rendered_video_url",
              type: "string",
            },
            {
              name: "response.status",
              customName: "render_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI quality review
      aiNode(
        "6",
        "AI: Review video quality",
        1240,
        "You are a video quality assurance reviewer. Evaluate the video production based on the script, scene descriptions, and rendered output. Check for brand consistency, message clarity, pacing, and emotional impact. Return JSON with quality_score (1-10), quality_passed (yes/no, threshold 7), issues_found (array of strings), and recommendations.",
        "Script: {script_text}. Scenes: {scene_descriptions}. Duration: {estimated_duration_seconds}s. Render status: {render_status}. Product: {product_name}. Customer: {customer_name}.",
        [
          { field: "quality_score", as: "quality_score", type: "number" },
          { field: "quality_passed", as: "quality_passed", type: "string" },
          { field: "issues_found", as: "issues_found", type: "string" },
          {
            field: "recommendations",
            as: "quality_recommendations",
            type: "string",
          },
        ],
      ),

      // 7. Quality condition
      n("7", "condition", "Did video pass quality review?", 1480, {
        conditionData: [
          {
            variable: "quality_passed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Human approval gate
      n("8", "approvalGate", "Human approval: Review final video", 1720, {
        approvalGateDetails: {
          approvalMessage:
            "Before/After Transformation Video for {product_name} featuring {customer_name} is ready for review. Quality score: {quality_score}/10. Video URL: {rendered_video_url}. Issues: {issues_found}. Please approve for publishing.",
          approvers: ["{content_manager_email}"],
          timeoutHours: 24,
        },
      }),

      // 9. Publish to Buffer
      n("9", "hitApi", "Publish video to social platforms (Buffer)", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_access_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            {
              key: "text",
              value:
                "See the amazing transformation with {product_name}! {customer_name} shares their journey. #transformation #results",
            },
            { key: "media[video]", value: "{rendered_video_url}" },
            { key: "profile_ids[]", value: "{buffer_profile_ids}" },
          ],
          variables: [
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
            {
              name: "success",
              customName: "buffer_published",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // quality passed -> human approval
      e("7", "6", "output_2"), // quality failed -> re-review (loop to fix)
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 889. BID ADJUSTMENT QUEUE (8 nodes)
  // ===================================================================

  "Bid Adjustment Queue": {
    summary:
      "Analyzes Google Ads performance on a schedule, calculates segment-level metrics, identifies optimization opportunities, prioritizes by impact, generates AI strategy recommendations, logs a bid adjustment report to Sheets, and delivers approval requests via Slack.",
    nodes: [
      trigger("Schedule: Bid adjustment analysis cycle", "schedule", {
        schedule: "0 6 * * 1-5 (weekdays at 6 AM)",
        howItStarts:
          "A scheduled job triggers analysis of Google Ads campaign segments to identify bid optimization opportunities based on recent performance data",
        setup:
          "Configure Google Ads API credentials. Set target CPA/ROAS thresholds per campaign type. Define segment granularity (device, location, audience, daypart).",
        inputFields: [],
      }),

      // 2. Fetch segmented performance data (Google Ads)
      n("2", "hitApi", "Fetch segmented performance data (Google Ads)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, ad_group.name, segments.device, segments.day_of_week, metrics.clicks, metrics.impressions, metrics.cost_micros, metrics.conversions, metrics.conversions_value FROM campaign WHERE segments.date DURING LAST_14_DAYS AND metrics.impressions > 100",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "segment_results_json",
              type: "string",
            },
            {
              name: "results.length",
              customName: "total_segments",
              type: "number",
            },
          ],
        },
      }),

      // 3. Segment-level metrics calculation (runCode)
      n(
        "3",
        "runCode",
        "Calculate segment-level metrics & identify opportunities",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Calculate per-segment performance metrics and identify bid adjustment opportunities
const results = JSON.parse(vars.segment_results_json || "[]");
const targetCpa = parseFloat(vars.target_cpa || "25");
const targetRoas = parseFloat(vars.target_roas || "4.0");
const opportunities = [];
let totalSpend = 0;
let totalConversions = 0;

for (const row of results) {
  const cost = (row.metrics_cost_micros || 0) / 1000000;
  const conversions = row.metrics_conversions || 0;
  const convValue = row.metrics_conversions_value || 0;
  const cpa = conversions > 0 ? cost / conversions : Infinity;
  const roas = cost > 0 ? convValue / cost : 0;
  totalSpend += cost;
  totalConversions += conversions;

  let adjustmentPct = 0;
  let reason = "";
  if (conversions >= 5 && cpa < targetCpa * 0.7) {
    adjustmentPct = Math.min(30, Math.round((1 - cpa / targetCpa) * 100));
    reason = "CPA " + cpa.toFixed(2) + " well below target " + targetCpa;
  } else if (conversions >= 3 && cpa > targetCpa * 1.3) {
    adjustmentPct = -Math.min(30, Math.round((cpa / targetCpa - 1) * 100));
    reason = "CPA " + cpa.toFixed(2) + " above target " + targetCpa;
  } else if (roas > targetRoas * 1.5) {
    adjustmentPct = Math.min(25, Math.round((roas / targetRoas - 1) * 50));
    reason = "ROAS " + roas.toFixed(2) + " exceeds target " + targetRoas;
  } else if (cost > 50 && roas < targetRoas * 0.5) {
    adjustmentPct = -Math.min(25, Math.round((1 - roas / targetRoas) * 50));
    reason = "ROAS " + roas.toFixed(2) + " below target " + targetRoas;
  }

  if (adjustmentPct !== 0) {
    const impact = Math.abs(adjustmentPct) * cost / 100;
    opportunities.push({
      campaign: row.campaign_name || "Unknown",
      segment: row.segments_device || row.segments_day_of_week || "all",
      current_cpa: cpa === Infinity ? "N/A" : cpa.toFixed(2),
      current_roas: roas.toFixed(2),
      adjustment_pct: adjustmentPct,
      reason: reason,
      estimated_impact: impact.toFixed(2)
    });
  }
}

opportunities.sort((a, b) => parseFloat(b.estimated_impact) - parseFloat(a.estimated_impact));
const topOpportunities = opportunities.slice(0, 20);

return {
  opportunity_count: opportunities.length,
  top_opportunities_json: JSON.stringify(topOpportunities),
  total_spend: totalSpend.toFixed(2),
  total_conversions: totalConversions,
  avg_cpa: totalConversions > 0 ? (totalSpend / totalConversions).toFixed(2) : "N/A",
  has_opportunities: opportunities.length > 0 ? "yes" : "no",
  analysis_date: new Date().toISOString().split("T")[0]
};`,
            outputVariables: [
              { name: "opportunity_count", type: "number" },
              { name: "top_opportunities_json", type: "string" },
              { name: "total_spend", type: "string" },
              { name: "total_conversions", type: "number" },
              { name: "avg_cpa", type: "string" },
              { name: "has_opportunities", type: "string" },
              { name: "analysis_date", type: "string" },
            ],
          },
        },
      ),

      // 4. Identify optimization opportunities (condition)
      n("4", "condition", "Are there bid adjustment opportunities?", 760, {
        conditionData: [
          {
            variable: "has_opportunities",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Impact prioritization (runCode)
      n("5", "runCode", "Prioritize opportunities by estimated impact", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Group and prioritize by impact tier
const opps = JSON.parse(vars.top_opportunities_json || "[]");
const high = opps.filter(o => parseFloat(o.estimated_impact) > 50);
const medium = opps.filter(o => parseFloat(o.estimated_impact) > 10 && parseFloat(o.estimated_impact) <= 50);
const low = opps.filter(o => parseFloat(o.estimated_impact) <= 10);
const totalEstSavings = opps.reduce((s, o) => s + parseFloat(o.estimated_impact), 0);
const summary = high.length + " high-impact, " + medium.length + " medium-impact, " + low.length + " low-impact adjustments";

return {
  high_impact_count: high.length,
  medium_impact_count: medium.length,
  low_impact_count: low.length,
  total_estimated_savings: totalEstSavings.toFixed(2),
  priority_summary: summary,
  prioritized_json: JSON.stringify({ high, medium, low })
};`,
          outputVariables: [
            { name: "high_impact_count", type: "number" },
            { name: "medium_impact_count", type: "number" },
            { name: "low_impact_count", type: "number" },
            { name: "total_estimated_savings", type: "string" },
            { name: "priority_summary", type: "string" },
            { name: "prioritized_json", type: "string" },
          ],
        },
      }),

      // 6. AI strategy review
      aiNode(
        "6",
        "AI: Generate bid adjustment strategy",
        1240,
        "You are a paid media strategist. Review the bid adjustment opportunities and generate an executive summary with recommendations. Consider seasonality, competitive dynamics, and budget constraints. Return JSON with executive_summary, recommended_actions (array of strings), risk_assessment, and confidence_level (high/medium/low).",
        "Date: {analysis_date}. Total spend: ${total_spend}. Avg CPA: ${avg_cpa}. Opportunities: {priority_summary}. Details: {prioritized_json}.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 7. Bid adjustment report (Sheets)
      sheetsNode(
        "7",
        "Log bid adjustment report (Sheets)",
        1480,
        "BidAdjustments",
        '[["{analysis_date}","{opportunity_count}","{total_spend}","{avg_cpa}","{priority_summary}","{total_estimated_savings}","{executive_summary}","{confidence_level}"]]',
      ),

      // 8. Approval delivery (Slack)
      slackNode(
        "8",
        "Deliver bid adjustment report for approval (Slack)",
        1720,
        ":chart_with_upwards_trend: *Bid Adjustment Report — {analysis_date}*\n\n*Summary:* {executive_summary}\n\n*Opportunities:* {opportunity_count} found ({priority_summary})\n*Est. savings:* ${total_estimated_savings}\n*Confidence:* {confidence_level}\n*Risk:* {risk_assessment}\n\n*Actions:* {recommended_actions}\n\nReview the full report in Sheets and approve adjustments.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // has opportunities -> prioritize
      e("4", "7", "output_2"), // no opportunities -> log clean report
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 890. BIRTHDAY CAPTURE AFTER SIGNUP (6 nodes)
  // ===================================================================

  "Birthday Capture After Signup": {
    summary:
      "Captures subscriber birthdays after signup for personalization. A new subscriber webhook triggers a birthday-on-file check via condition, AI generates a personalized birthday capture email, the email is sent, submission processing extracts the birthday date, and the profile is updated in HubSpot.",
    nodes: [
      trigger("Webhook: New subscriber registered", "webhook_app", {
        source: "Website / HubSpot",
        event: "subscriber.created",
        howItStarts:
          "A new subscriber signs up via the website or is added to HubSpot, triggering a workflow to capture their birthday for future personalization campaigns",
        setup:
          "Configure HubSpot workflow or website signup form to POST new subscriber events to this workflow URL. Include email, first name, and any existing profile data.",
        inputFields: [],
      }),

      // 2. Extract subscriber data + check for existing birthday
      aiNode(
        "2",
        "Extract subscriber data",
        280,
        "You are a data extraction assistant. Extract the subscriber email, first name, and check if a birthday/date_of_birth field exists in the payload. Return JSON with subscriber_email, first_name, has_birthday (yes/no), and existing_birthday (empty string if none).",
        "Extract subscriber info from: {webhook_payload}",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "has_birthday", as: "has_birthday", type: "string" },
          {
            field: "existing_birthday",
            as: "existing_birthday",
            type: "string",
          },
        ],
      ),

      // 3. Birthday check condition
      n(
        "3",
        "condition",
        "Does subscriber already have a birthday on file?",
        520,
        {
          conditionData: [
            {
              variable: "has_birthday",
              value: "no",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 4. AI birthday capture email content
      aiNode(
        "4",
        "AI: Generate personalized birthday capture email",
        760,
        "You are an email copywriter. Write a friendly, short email asking the subscriber for their birthday so you can send them a special gift. Be warm but not pushy. Include a clear CTA link. Return JSON with email_subject, email_body_html (with placeholder {birthday_form_url} for the form link), and preview_text.",
        "Subscriber first name: {first_name}. Brand tone: friendly and personal.",
        [
          {
            field: "email_subject",
            as: "birthday_email_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "birthday_email_body",
            type: "string",
          },
          {
            field: "preview_text",
            as: "birthday_preview_text",
            type: "string",
          },
        ],
      ),

      // 5. Send birthday request email
      emailNode(
        "5",
        "Send birthday capture email",
        1000,
        "{subscriber_email}",
        "{birthday_email_subject}",
        "{birthday_email_body}",
      ),

      // 6. Update profile in HubSpot with birthday
      hubspotPostNode(
        "6",
        "Update subscriber profile with birthday (HubSpot)",
        1240,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{subscriber_email}"}]}]',
          },
          { key: "properties", value: '["email","date_of_birth"]' },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.date_of_birth",
            customName: "stored_birthday",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // no birthday on file -> send capture email
      e("3", "6", "output_2"), // already has birthday -> just verify in HubSpot
      e("4", "5"),
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 891. BIRTHDAY VIP GIFT (7 nodes)
  // ===================================================================

  "Birthday VIP Gift": {
    summary:
      "Celebrates VIP customer birthdays with personalized gifts. A daily schedule fetches VIP customers with birthdays today from HubSpot, runCode selects an appropriate gift based on customer tier, AI generates a personalized birthday card, Shopify triggers gift fulfillment, a birthday email is sent, and the gift is logged to Sheets with a CS Slack notification.",
    nodes: [
      trigger("Schedule: Daily VIP birthday check", "schedule", {
        schedule: "0 7 * * * (daily at 7 AM)",
        howItStarts:
          "A daily scheduled job checks for VIP customers whose birthday is today to trigger personalized gift fulfillment and celebration messaging",
        setup:
          "Ensure HubSpot contacts have date_of_birth and vip_tier properties populated. Configure Shopify gift product variants. Set up gift budget tiers per VIP level.",
        inputFields: [],
      }),

      // 2. Fetch VIP customers with today's birthday (HubSpot)
      hubspotPostNode(
        "2",
        "Fetch VIP birthday customers (HubSpot)",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"vip_tier","operator":"HAS_PROPERTY"},{"propertyName":"date_of_birth_month_day","operator":"EQ","value":"{today_month_day}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","vip_tier","date_of_birth","lifetime_value","favorite_category"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "total", customName: "birthday_vip_count", type: "number" },
          { name: "results", customName: "birthday_vips_json", type: "string" },
        ],
      ),

      // 3. Gift selection (runCode)
      n("3", "runCode", "Select gift based on VIP tier and preferences", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select appropriate gift based on VIP tier and customer preferences
const vips = JSON.parse(vars.birthday_vips_json || "[]");
const giftTiers = {
  platinum: { budget: 100, gifts: ["Premium Gift Box", "Exclusive Collection Set", "VIP Experience Voucher"] },
  gold: { budget: 50, gifts: ["Curated Gift Set", "Limited Edition Item", "Gift Card + Bonus"] },
  silver: { budget: 25, gifts: ["Birthday Gift Card", "Surprise Sample Box", "Loyalty Bonus Points"] }
};
const selections = [];
for (const vip of vips) {
  const props = vip.properties || {};
  const tier = (props.vip_tier || "silver").toLowerCase();
  const tierConfig = giftTiers[tier] || giftTiers.silver;
  const favCategory = props.favorite_category || "general";
  const giftIndex = Math.abs(((props.firstname || "").length + (props.lastname || "").length) % tierConfig.gifts.length);
  selections.push({
    email: props.email,
    first_name: props.firstname || "Valued Customer",
    last_name: props.lastname || "",
    vip_tier: tier,
    gift_name: tierConfig.gifts[giftIndex],
    gift_budget: tierConfig.budget,
    favorite_category: favCategory,
    contact_id: vip.id
  });
}
const firstVip = selections[0] || {};
return {
  gift_selections_json: JSON.stringify(selections),
  total_gifts: selections.length,
  total_gift_budget: selections.reduce((s, g) => s + g.gift_budget, 0),
  first_email: firstVip.email || "",
  first_name: firstVip.first_name || "",
  first_gift_name: firstVip.gift_name || "",
  first_vip_tier: firstVip.vip_tier || "",
  first_contact_id: firstVip.contact_id || ""
};`,
          outputVariables: [
            { name: "gift_selections_json", type: "string" },
            { name: "total_gifts", type: "number" },
            { name: "total_gift_budget", type: "number" },
            { name: "first_email", type: "string" },
            { name: "first_name", type: "string" },
            { name: "first_gift_name", type: "string" },
            { name: "first_vip_tier", type: "string" },
            { name: "first_contact_id", type: "string" },
          ],
        },
      }),

      // 4. AI personalized birthday card
      aiNode(
        "4",
        "AI: Generate personalized birthday card",
        760,
        "You are a luxury brand copywriter. Write a warm, personalized birthday message for a VIP customer. Include their name, acknowledge their VIP status, mention the gift being sent, and express genuine gratitude for their loyalty. Return JSON with card_message, email_subject, email_body_html (beautifully formatted birthday email with gift details), and social_message (short birthday wish for optional social post).",
        "Customer: {first_name}. VIP tier: {first_vip_tier}. Gift: {first_gift_name}.",
        [
          { field: "card_message", as: "card_message", type: "string" },
          { field: "email_subject", as: "birthday_subject", type: "string" },
          {
            field: "email_body_html",
            as: "birthday_email_html",
            type: "string",
          },
          {
            field: "social_message",
            as: "social_birthday_message",
            type: "string",
          },
        ],
      ),

      // 5. Gift fulfillment trigger (Shopify)
      n("5", "hitApi", "Trigger gift fulfillment (Shopify)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "order",
              value:
                '{"email":"{first_email}","note":"VIP Birthday Gift - {first_gift_name} for {first_name} ({first_vip_tier} tier)","tags":"birthday-gift,vip-{first_vip_tier}","line_items":[{"title":"{first_gift_name}","quantity":1,"price":"0.00"}],"financial_status":"paid","send_receipt":false}',
            },
          ],
          variables: [
            {
              name: "order.id",
              customName: "shopify_order_id",
              type: "string",
            },
            {
              name: "order.order_number",
              customName: "shopify_order_number",
              type: "string",
            },
            {
              name: "order.created_at",
              customName: "order_created_at",
              type: "string",
            },
          ],
        },
      }),

      // 6. Send birthday email
      emailNode(
        "6",
        "Send personalized birthday email",
        1240,
        "{first_email}",
        "{birthday_subject}",
        "{birthday_email_html}",
      ),

      // 7. Gift logging + CS notification (Sheets + Slack)
      sheetsNode(
        "7",
        "Log birthday gift (Sheets) + notify CS (Slack)",
        1480,
        "BirthdayGifts",
        '[["{order_created_at}","{first_name}","{first_email}","{first_vip_tier}","{first_gift_name}","{shopify_order_number}","{total_gift_budget}","{card_message}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 892. BRAND MENTION SOCIAL LISTENER (8 nodes)
  // ===================================================================

  "Brand Mention Social Listener": {
    summary:
      "Monitors brand mentions across social platforms, classifies sentiment, escalates crises, curates UGC opportunities, and delivers daily digests. A scheduled monitor detects mentions via SerpAPI, AI classifies sentiment, a crisis condition triggers PR escalation via Slack, positive mentions are queued for engagement, UGC opportunities are curated, and a daily digest email summarizes all activity.",
    nodes: [
      trigger("Schedule: Continuous brand mention monitoring", "schedule", {
        schedule: "0 */2 * * * (every 2 hours)",
        howItStarts:
          "A scheduled job scans social media platforms, news, and forums for brand mentions using SerpAPI to detect sentiment shifts, crisis signals, and user-generated content opportunities",
        setup:
          "Configure SerpAPI key and brand name/keywords to monitor. Set crisis keywords (e.g., lawsuit, recall, scandal). Define engagement thresholds for UGC curation.",
        inputFields: [],
      }),

      // 2. Social mention detection (SerpAPI)
      n("2", "hitApi", "Detect brand mentions (SerpAPI)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?engine=google&q=%22{brand_name}%22&tbm=nws&api_key={serpapi_key}&num=20&tbs=qdr:h2",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "mentions_json",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_mentions",
              type: "number",
            },
            {
              name: "search_metadata.created_at",
              customName: "scan_timestamp",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI sentiment classification
      aiNode(
        "3",
        "AI: Classify sentiment for all mentions",
        520,
        "You are a brand monitoring analyst. Analyze each mention for sentiment (positive/negative/neutral), detect crisis signals (keywords: recall, lawsuit, scandal, outage, data breach, complaint viral), identify UGC opportunities (positive reviews, user stories, testimonials), and compute an overall brand health score (1-10). Return JSON with overall_sentiment, brand_health_score, crisis_detected (yes/no), crisis_mentions (array), positive_mentions (array), ugc_opportunities (array), mention_count, negative_count, positive_count, neutral_count.",
        "Brand: {brand_name}. Mentions: {mentions_json}.",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          {
            field: "brand_health_score",
            as: "brand_health_score",
            type: "number",
          },
          { field: "crisis_detected", as: "crisis_detected", type: "string" },
          {
            field: "crisis_mentions",
            as: "crisis_mentions_json",
            type: "string",
          },
          {
            field: "positive_mentions",
            as: "positive_mentions_json",
            type: "string",
          },
          {
            field: "ugc_opportunities",
            as: "ugc_opportunities_json",
            type: "string",
          },
          {
            field: "mention_count",
            as: "classified_mention_count",
            type: "number",
          },
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "positive_count", as: "positive_count", type: "number" },
        ],
      ),

      // 4. Crisis signal condition
      n("4", "condition", "Was a crisis signal detected?", 760, {
        conditionData: [
          {
            variable: "crisis_detected",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. PR escalation Slack alert (True path)
      slackNode(
        "5",
        "PR escalation: Crisis mention alert (Slack)",
        1000,
        ":rotating_light: *BRAND CRISIS ALERT*\n\n*Brand Health Score:* {brand_health_score}/10\n*Crisis Mentions:* {crisis_mentions_json}\n*Overall Sentiment:* {overall_sentiment}\n*Negative Mentions:* {negative_count}\n\nImmediate PR team review required. Escalate to communications director.",
      ),

      // 6. Positive engagement queue
      n(
        "6",
        "runCode",
        "Queue positive mentions for engagement",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Process positive mentions for engagement and UGC curation
const positive = JSON.parse(vars.positive_mentions_json || "[]");
const ugc = JSON.parse(vars.ugc_opportunities_json || "[]");
const engagementQueue = positive.map((m, i) => ({
  index: i + 1,
  title: m.title || m.text || "Mention " + (i+1),
  source: m.source || "unknown",
  url: m.link || m.url || "",
  action: "engage"
}));
const ugcCandidates = ugc.map((u, i) => ({
  index: i + 1,
  title: u.title || u.text || "UGC " + (i+1),
  source: u.source || "unknown",
  url: u.link || u.url || "",
  action: "curate_for_reshare"
}));
return {
  engagement_queue_json: JSON.stringify(engagementQueue),
  engagement_count: engagementQueue.length,
  ugc_candidate_json: JSON.stringify(ugcCandidates),
  ugc_count: ugcCandidates.length,
  queue_timestamp: new Date().toISOString()
};`,
            outputVariables: [
              { name: "engagement_queue_json", type: "string" },
              { name: "engagement_count", type: "number" },
              { name: "ugc_candidate_json", type: "string" },
              { name: "ugc_count", type: "number" },
              { name: "queue_timestamp", type: "string" },
            ],
          },
        },
        400,
      ),

      // 7. UGC opportunity curation
      aiNode(
        "7",
        "AI: Curate UGC opportunities",
        1240,
        "You are a social media strategist. Review the UGC candidates and create a curation brief for the social team. For each opportunity, suggest a reshare strategy, caption draft, and permission request template. Return JSON with curated_ugc (array with strategy per item), top_ugc_pick (best single piece for reshare), and curation_notes.",
        "UGC candidates: {ugc_candidate_json}. Brand: {brand_name}. Positive mentions: {engagement_count}.",
        [
          { field: "curated_ugc", as: "curated_ugc_json", type: "string" },
          { field: "top_ugc_pick", as: "top_ugc_pick", type: "string" },
          { field: "curation_notes", as: "ugc_curation_notes", type: "string" },
        ],
      ),

      // 8. Daily mention digest (email)
      emailNode(
        "8",
        "Send daily brand mention digest",
        1480,
        "{marketing_team_email}",
        "Brand Mention Digest — {brand_name} | Health: {brand_health_score}/10 | {scan_timestamp}",
        "<h2>Brand Mention Digest</h2><p><strong>Health Score:</strong> {brand_health_score}/10</p><p><strong>Overall Sentiment:</strong> {overall_sentiment}</p><p><strong>Total Mentions:</strong> {classified_mention_count} ({positive_count} positive, {negative_count} negative)</p><h3>Engagement Queue</h3><p>{engagement_count} positive mentions queued for engagement</p><h3>UGC Opportunities</h3><p>{ugc_count} candidates curated. Top pick: {top_ugc_pick}</p><p>{ugc_curation_notes}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // crisis detected -> PR escalation
      e("4", "6", "output_2"), // no crisis -> positive engagement queue
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 893. BRAZE A/B TEST AUTOMATION (9 nodes)
  // ===================================================================

  "Braze A/B Test Automation": {
    summary:
      "Automates the full lifecycle of Braze A/B tests from creation to auto-deployment. A campaign creation webhook triggers Braze test variant configuration, statistical parameters are set via runCode, the A/B test is launched, progress is monitored, a significance condition gates auto-deployment, the winner is deployed, results are logged to Sheets, and stakeholders are notified via Slack.",
    nodes: [
      trigger("Webhook: A/B test campaign creation request", "webhook_app", {
        source: "Marketing Portal / Braze",
        event: "campaign.ab_test.requested",
        howItStarts:
          "A marketing team member submits an A/B test request with campaign details, test variants, audience segment, and success metrics to be tested in Braze",
        setup:
          "Configure your marketing portal to POST A/B test requests to this workflow URL. Include campaign name, variant details, target segment, and KPIs.",
        inputFields: [],
      }),

      // 2. Extract + configure Braze test variants
      aiNode(
        "2",
        "Extract test config & prepare Braze variants",
        280,
        "You are a marketing ops assistant. Extract the A/B test configuration from the webhook payload: campaign name, variant A content, variant B content, target segment, primary KPI (open_rate/click_rate/conversion_rate), and test duration. Return JSON.",
        "Extract A/B test config from: {webhook_payload}",
        [
          { field: "campaign_name", as: "ab_campaign_name", type: "string" },
          {
            field: "variant_a_content",
            as: "variant_a_content",
            type: "string",
          },
          {
            field: "variant_b_content",
            as: "variant_b_content",
            type: "string",
          },
          { field: "target_segment", as: "target_segment", type: "string" },
          { field: "primary_kpi", as: "primary_kpi", type: "string" },
          {
            field: "test_duration_hours",
            as: "test_duration_hours",
            type: "number",
          },
        ],
      ),

      // 3. Statistical parameter setup (runCode)
      n("3", "runCode", "Calculate statistical parameters", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate statistical parameters for the A/B test
const confidenceLevel = 0.95;
const zScore = 1.96; // 95% confidence
const mde = parseFloat(vars.minimum_detectable_effect || "0.05"); // 5% minimum detectable effect
const baselineRate = parseFloat(vars.baseline_rate || "0.20"); // 20% baseline
const power = 0.80;

// Sample size calculation (simplified)
const p1 = baselineRate;
const p2 = baselineRate * (1 + mde);
const pooledP = (p1 + p2) / 2;
const numerator = Math.pow(zScore * Math.sqrt(2 * pooledP * (1 - pooledP)) + 1.28 * Math.sqrt(p1*(1-p1) + p2*(1-p2)), 2);
const denominator = Math.pow(p2 - p1, 2);
const samplePerVariant = Math.ceil(numerator / denominator);
const totalSample = samplePerVariant * 2;
const testAllocation = 50; // 50/50 split

return {
  sample_size_per_variant: samplePerVariant,
  total_sample_required: totalSample,
  confidence_level: (confidenceLevel * 100).toFixed(0),
  z_score: zScore.toFixed(2),
  minimum_detectable_effect: (mde * 100).toFixed(1),
  baseline_rate: (baselineRate * 100).toFixed(1),
  test_allocation: testAllocation + "/" + testAllocation,
  stat_params_json: JSON.stringify({ samplePerVariant, totalSample, confidenceLevel, zScore, mde, power })
};`,
          outputVariables: [
            { name: "sample_size_per_variant", type: "number" },
            { name: "total_sample_required", type: "number" },
            { name: "confidence_level", type: "string" },
            { name: "z_score", type: "string" },
            { name: "minimum_detectable_effect", type: "string" },
            { name: "test_allocation", type: "string" },
            { name: "stat_params_json", type: "string" },
          ],
        },
      }),

      // 4. Launch A/B test in Braze
      brazePostNode(
        "4",
        "Launch A/B test in Braze",
        760,
        "campaigns/trigger/send",
        [
          { key: "campaign_id", value: "{braze_campaign_id}" },
          {
            key: "recipients",
            value:
              '[{"external_user_id":"{target_segment}","trigger_properties":{"variant":"A","content":"{variant_a_content}"}},{"external_user_id":"{target_segment}","trigger_properties":{"variant":"B","content":"{variant_b_content}"}}]',
          },
          { key: "broadcast", value: "false" },
        ],
        [
          {
            name: "dispatch_id",
            customName: "braze_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "braze_launch_status",
            type: "string",
          },
        ],
      ),

      // 5. Test progress monitoring (delay + check)
      n("5", "delayAction", "Wait for test duration to complete", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "{test_duration_hours}",
          delayUnit: "hours",
          description:
            "Waiting for A/B test to accumulate sufficient data before checking statistical significance",
        },
      }),

      // 6. Check statistical significance
      n("6", "runCode", "Check statistical significance of results", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if A/B test results are statistically significant
const variantARate = parseFloat(vars.variant_a_rate || "0.22");
const variantBRate = parseFloat(vars.variant_b_rate || "0.25");
const variantASample = parseInt(vars.variant_a_sample || "1000");
const variantBSample = parseInt(vars.variant_b_sample || "1000");

const pooledRate = (variantARate * variantASample + variantBRate * variantBSample) / (variantASample + variantBSample);
const se = Math.sqrt(pooledRate * (1 - pooledRate) * (1/variantASample + 1/variantBSample));
const zStat = Math.abs(variantARate - variantBRate) / se;
const isSignificant = zStat > 1.96;
const winner = variantARate > variantBRate ? "A" : "B";
const winnerRate = Math.max(variantARate, variantBRate);
const loserRate = Math.min(variantARate, variantBRate);
const lift = loserRate > 0 ? ((winnerRate - loserRate) / loserRate * 100).toFixed(1) : "N/A";

return {
  is_significant: isSignificant ? "yes" : "no",
  z_statistic: zStat.toFixed(3),
  winning_variant: winner,
  variant_a_rate_pct: (variantARate * 100).toFixed(2),
  variant_b_rate_pct: (variantBRate * 100).toFixed(2),
  lift_percentage: lift,
  total_samples_tested: variantASample + variantBSample,
  significance_summary: isSignificant ? "Variant " + winner + " wins with " + lift + "% lift (z=" + zStat.toFixed(2) + ", p<0.05)" : "No significant difference detected (z=" + zStat.toFixed(2) + ")"
};`,
          outputVariables: [
            { name: "is_significant", type: "string" },
            { name: "z_statistic", type: "string" },
            { name: "winning_variant", type: "string" },
            { name: "variant_a_rate_pct", type: "string" },
            { name: "variant_b_rate_pct", type: "string" },
            { name: "lift_percentage", type: "string" },
            { name: "total_samples_tested", type: "number" },
            { name: "significance_summary", type: "string" },
          ],
        },
      }),

      // 7. Significance condition
      n("7", "condition", "Is the result statistically significant?", 1480, {
        conditionData: [
          {
            variable: "is_significant",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Test results report (Sheets)
      sheetsNode(
        "8",
        "Log A/B test results (Sheets)",
        1720,
        "ABTestResults",
        '[["{ab_campaign_name}","{winning_variant}","{variant_a_rate_pct}","{variant_b_rate_pct}","{lift_percentage}","{z_statistic}","{is_significant}","{total_samples_tested}","{significance_summary}"]]',
      ),

      // 9. Stakeholder distribution (Slack)
      slackNode(
        "9",
        "Notify stakeholders of A/B test results (Slack)",
        1960,
        ":test_tube: *A/B Test Results: {ab_campaign_name}*\n\n*Result:* {significance_summary}\n*Variant A:* {variant_a_rate_pct}% | *Variant B:* {variant_b_rate_pct}%\n*Winner:* Variant {winning_variant} ({lift_percentage}% lift)\n*Samples:* {total_samples_tested}\n\nResults logged to Sheets. Winner auto-deployed to full audience.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // significant -> log + deploy winner
      e("7", "8", "output_2"), // not significant -> log + notify (extend test)
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 894. BRAZE CHURN PREVENTION FLOW (9 nodes)
  // ===================================================================

  "Braze Churn Prevention Flow": {
    summary:
      "Identifies at-risk users daily and deploys Braze retention canvases with personalized content. A daily scan triggers user engagement data fetch, churn risk scores are calculated via runCode, users are segmented by risk tier, a condition routes high-risk users, Braze retention canvas is deployed, AI generates personalized retention content, responses are monitored, and a prevention report is logged to Sheets.",
    nodes: [
      trigger("Schedule: Daily churn risk scan", "schedule", {
        schedule: "0 5 * * * (daily at 5 AM)",
        howItStarts:
          "A daily scheduled job analyzes user engagement data to identify churn risk and proactively deploy retention campaigns through Braze canvases",
        setup:
          "Configure Braze API credentials. Set churn risk thresholds. Define retention canvas IDs for each risk tier. Ensure user engagement events (last_active, purchase_count, session_count) are tracked in Braze.",
        inputFields: [],
      }),

      // 2. User engagement data fetch
      brazePostNode(
        "2",
        "Fetch user engagement data (Braze)",
        280,
        "users/export/segment",
        [
          { key: "segment_id", value: "{braze_active_users_segment_id}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","custom_attributes","last_active","total_revenue","purchase_count"]',
          },
        ],
        [
          { name: "users", customName: "user_engagement_json", type: "string" },
          { name: "message", customName: "export_status", type: "string" },
        ],
      ),

      // 3. Churn risk score calculation (runCode)
      n("3", "runCode", "Calculate churn risk scores", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate churn risk score for each user based on engagement signals
const users = JSON.parse(vars.user_engagement_json || "[]");
const now = Date.now();
const results = { high: [], medium: [], low: [] };
let totalAtRisk = 0;

for (const user of users) {
  const lastActive = new Date(user.last_active || 0).getTime();
  const daysSinceActive = (now - lastActive) / 86400000;
  const purchaseCount = user.purchase_count || 0;
  const totalRevenue = user.total_revenue || 0;
  const attrs = user.custom_attributes || {};
  const sessionCount30d = attrs.sessions_last_30d || 0;
  const emailOpens30d = attrs.email_opens_last_30d || 0;

  // Weighted risk score (0-100, higher = more at risk)
  let riskScore = 0;
  riskScore += Math.min(40, daysSinceActive * 2); // inactivity weight
  riskScore += sessionCount30d < 3 ? 20 : sessionCount30d < 7 ? 10 : 0;
  riskScore += emailOpens30d < 1 ? 15 : emailOpens30d < 3 ? 7 : 0;
  riskScore += purchaseCount < 1 ? 15 : purchaseCount < 3 ? 7 : 0;
  riskScore += totalRevenue < 10 ? 10 : totalRevenue < 50 ? 5 : 0;
  riskScore = Math.min(100, Math.round(riskScore));

  const tier = riskScore >= 70 ? "high" : riskScore >= 40 ? "medium" : "low";
  if (tier !== "low") totalAtRisk++;
  results[tier].push({
    external_id: user.external_id,
    email: user.email,
    risk_score: riskScore,
    days_inactive: Math.round(daysSinceActive),
    purchase_count: purchaseCount,
    tier: tier
  });
}

return {
  high_risk_count: results.high.length,
  medium_risk_count: results.medium.length,
  low_risk_count: results.low.length,
  total_at_risk: totalAtRisk,
  high_risk_users_json: JSON.stringify(results.high.slice(0, 50)),
  medium_risk_users_json: JSON.stringify(results.medium.slice(0, 50)),
  has_high_risk: results.high.length > 0 ? "yes" : "no",
  scan_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "high_risk_count", type: "number" },
            { name: "medium_risk_count", type: "number" },
            { name: "low_risk_count", type: "number" },
            { name: "total_at_risk", type: "number" },
            { name: "high_risk_users_json", type: "string" },
            { name: "medium_risk_users_json", type: "string" },
            { name: "has_high_risk", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 4. Risk tier segmentation (AI)
      aiNode(
        "4",
        "AI: Determine retention strategies per tier",
        760,
        "You are a retention marketing strategist. Based on the churn risk data, determine the best retention strategy for high-risk and medium-risk users. Consider incentive types, channel preferences, timing, and messaging tone. Return JSON with high_risk_strategy (object with incentive, channel, message_tone, urgency), medium_risk_strategy (same shape), overall_approach, and predicted_save_rate.",
        "High risk users: {high_risk_count}. Medium risk: {medium_risk_count}. High risk sample: {high_risk_users_json}.",
        [
          {
            field: "high_risk_strategy",
            as: "high_risk_strategy_json",
            type: "string",
          },
          {
            field: "medium_risk_strategy",
            as: "medium_risk_strategy_json",
            type: "string",
          },
          {
            field: "overall_approach",
            as: "retention_approach",
            type: "string",
          },
          {
            field: "predicted_save_rate",
            as: "predicted_save_rate",
            type: "string",
          },
        ],
      ),

      // 5. Risk condition
      n(
        "5",
        "condition",
        "Are there high-risk users requiring immediate action?",
        1000,
        {
          conditionData: [
            {
              variable: "has_high_risk",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Braze retention canvas deployment
      brazePostNode(
        "6",
        "Deploy Braze retention canvas for high-risk users",
        1240,
        "canvas/trigger/send",
        [
          { key: "canvas_id", value: "{braze_retention_canvas_id}" },
          { key: "recipients", value: "{high_risk_users_json}" },
          {
            key: "canvas_entry_properties",
            value:
              '{"risk_tier":"high","strategy":"{high_risk_strategy_json}"}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "retention_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "canvas_deployment_status",
            type: "string",
          },
        ],
      ),

      // 7. AI personalized retention content
      aiNode(
        "7",
        "AI: Generate personalized retention content",
        1480,
        "You are a retention copywriter. Create compelling win-back content for at-risk users. Write an email subject line, body with personalized incentive, and a push notification message. The tone should match the risk level urgency. Return JSON with retention_subject, retention_body_html, push_message, and incentive_details.",
        "Strategy: {high_risk_strategy_json}. At-risk users: {high_risk_count}. Approach: {retention_approach}.",
        [
          {
            field: "retention_subject",
            as: "retention_subject",
            type: "string",
          },
          {
            field: "retention_body_html",
            as: "retention_body_html",
            type: "string",
          },
          {
            field: "push_message",
            as: "retention_push_message",
            type: "string",
          },
          {
            field: "incentive_details",
            as: "incentive_details",
            type: "string",
          },
        ],
      ),

      // 8. Retention response monitoring (delay)
      n("8", "delayAction", "Monitor retention campaign responses", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "48",
          delayUnit: "hours",
          description:
            "Allow 48 hours for retention campaign responses before measuring effectiveness",
        },
      }),

      // 9. Churn prevention report (Sheets)
      sheetsNode(
        "9",
        "Log churn prevention report (Sheets)",
        1960,
        "ChurnPrevention",
        '[["{scan_date}","{high_risk_count}","{medium_risk_count}","{total_at_risk}","{retention_approach}","{predicted_save_rate}","{canvas_deployment_status}","{incentive_details}","{retention_dispatch_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high risk users exist -> deploy canvas
      e("5", "9", "output_2"), // no high risk -> log report only
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 895. BRAZE CONTENT CARD UPDATER (7 nodes)
  // ===================================================================

  "Braze Content Card Updater": {
    summary:
      "Rotates and personalizes Braze content cards on a schedule. A scheduled rotation fetches the content calendar, AI personalizes card content, Braze content cards are updated, a priority promotion condition routes special offers, card rendering is validated via runCode, and a content card report is sent to Slack.",
    nodes: [
      trigger("Schedule: Content card rotation", "schedule", {
        schedule: "0 9 * * * (daily at 9 AM)",
        howItStarts:
          "A scheduled job rotates Braze content cards based on the content calendar, personalizing messages and prioritizing promotional content for the day",
        setup:
          "Configure Braze API credentials. Set up a Google Sheets or CMS-based content calendar with card titles, descriptions, images, CTAs, and priority flags. Define content card campaign IDs in Braze.",
        inputFields: [],
      }),

      // 2. Content calendar fetch
      n("2", "hitApi", "Fetch today's content calendar (Sheets)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{content_calendar_sheet_id}/values/ContentCards!A2:H50",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            {
              name: "values",
              customName: "content_calendar_json",
              type: "string",
            },
            { name: "range", customName: "calendar_range", type: "string" },
          ],
        },
      }),

      // 3. AI card personalization
      aiNode(
        "3",
        "AI: Personalize content card messaging",
        520,
        "You are a content strategist. Review the content calendar entries and personalize each card for maximum engagement. Optimize headlines, descriptions, and CTAs. Identify which card should be marked as priority promo. Return JSON with personalized_cards (array with title, description, cta_text, image_url, is_priority), priority_card_index, total_cards, and has_priority_promo (yes/no).",
        "Content calendar: {content_calendar_json}. Today's date: {today_date}. Brand tone: engaging and action-oriented.",
        [
          {
            field: "personalized_cards",
            as: "personalized_cards_json",
            type: "string",
          },
          {
            field: "priority_card_index",
            as: "priority_card_index",
            type: "number",
          },
          { field: "total_cards", as: "total_cards_today", type: "number" },
          {
            field: "has_priority_promo",
            as: "has_priority_promo",
            type: "string",
          },
        ],
      ),

      // 4. Braze content card update
      brazePostNode(
        "4",
        "Update Braze content cards",
        760,
        "content_cards/create",
        [
          { key: "cards", value: "{personalized_cards_json}" },
          { key: "segment_id", value: "{braze_all_users_segment_id}" },
        ],
        [
          { name: "message", customName: "card_update_status", type: "string" },
          {
            name: "cards_created",
            customName: "cards_created_count",
            type: "number",
          },
        ],
      ),

      // 5. Priority promo condition
      n("5", "condition", "Is there a priority promotional card today?", 1000, {
        conditionData: [
          {
            variable: "has_priority_promo",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Card rendering validation (runCode)
      n("6", "runCode", "Validate card rendering and format", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate content card rendering specifications
const cards = JSON.parse(vars.personalized_cards_json || "[]");
const issues = [];
let validCount = 0;

for (let i = 0; i < cards.length; i++) {
  const card = cards[i];
  if (!card.title || card.title.length > 50) issues.push("Card " + (i+1) + ": title missing or >50 chars");
  if (!card.description || card.description.length > 150) issues.push("Card " + (i+1) + ": description missing or >150 chars");
  if (!card.cta_text || card.cta_text.length > 25) issues.push("Card " + (i+1) + ": CTA missing or >25 chars");
  if (!card.image_url) issues.push("Card " + (i+1) + ": image URL missing");
  if (!issues.some(iss => iss.startsWith("Card " + (i+1)))) validCount++;
}
const allValid = issues.length === 0;
const hasPriority = vars.has_priority_promo === "yes";

return {
  validation_passed: allValid ? "yes" : "no",
  valid_card_count: validCount,
  issue_count: issues.length,
  issues_summary: issues.length > 0 ? issues.join("; ") : "All cards pass validation",
  priority_promo_active: hasPriority ? "Priority promo card is live at index " + vars.priority_card_index : "No priority promo today",
  render_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "valid_card_count", type: "number" },
            { name: "issue_count", type: "number" },
            { name: "issues_summary", type: "string" },
            { name: "priority_promo_active", type: "string" },
            { name: "render_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Content card report (Slack)
      slackNode(
        "7",
        "Content card rotation report (Slack)",
        1480,
        ":card_index: *Content Card Rotation Complete*\n\n*Cards Updated:* {total_cards_today}\n*Valid:* {valid_card_count} | *Issues:* {issue_count}\n*Priority:* {priority_promo_active}\n*Validation:* {issues_summary}\n*Status:* {card_update_status}\n*Timestamp:* {render_timestamp}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // priority promo -> validate rendering
      e("5", "6", "output_2"), // no priority -> still validate
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 896. BRAZE CROSS-CHANNEL JOURNEY (10 nodes)
  // ===================================================================

  "Braze Cross-Channel Journey": {
    summary:
      "Orchestrates a multi-touchpoint customer journey across email, push, and SMS using Braze. A customer event triggers profile and preference fetch, AI determines optimal channel sequence, channel-specific content is generated, email is sent first, a delay monitors engagement, a condition checks engagement, push notification serves as fallback, SMS is the final fallback, and journey attribution is logged to Sheets.",
    nodes: [
      trigger("Webhook: Customer journey event trigger", "webhook_app", {
        source: "Braze / Website / App",
        event: "customer.journey.trigger",
        howItStarts:
          "A customer event (purchase, cart abandonment, milestone, etc.) triggers a cross-channel journey that intelligently sequences email, push, and SMS based on the customer's channel preferences and engagement patterns",
        setup:
          "Configure Braze or your app to POST customer events to this workflow URL. Include external_user_id, event type, and any contextual data (product, cart value, etc.).",
        inputFields: [],
      }),

      // 2. Profile + preferences fetch
      aiNode(
        "2",
        "Extract event data & fetch profile context",
        280,
        "You are a data extraction assistant. Extract the customer external_user_id, event_type, event_context (product/cart/milestone details), and any channel preferences from the webhook payload. Return JSON.",
        "Extract customer journey data from: {webhook_payload}",
        [
          {
            field: "external_user_id",
            as: "customer_external_id",
            type: "string",
          },
          { field: "event_type", as: "journey_event_type", type: "string" },
          { field: "event_context", as: "event_context", type: "string" },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
        ],
      ),

      // 3. AI channel sequence determination
      aiNode(
        "3",
        "AI: Determine optimal channel sequence",
        520,
        "You are a cross-channel journey orchestrator. Based on the event type, customer preferences, and context, determine the optimal channel sequence (email first, push second, SMS last). Customize timing between touches and urgency level. Return JSON with primary_channel, secondary_channel, tertiary_channel, delay_hours_after_primary, delay_hours_after_secondary, urgency_level (high/medium/low), and journey_rationale.",
        "Event: {journey_event_type}. Context: {event_context}. Preferred channel: {preferred_channel}. Has email: {customer_email}. Has phone: {customer_phone}.",
        [
          { field: "primary_channel", as: "primary_channel", type: "string" },
          {
            field: "secondary_channel",
            as: "secondary_channel",
            type: "string",
          },
          { field: "tertiary_channel", as: "tertiary_channel", type: "string" },
          {
            field: "delay_hours_after_primary",
            as: "delay_after_primary",
            type: "number",
          },
          {
            field: "delay_hours_after_secondary",
            as: "delay_after_secondary",
            type: "number",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "journey_rationale",
            as: "journey_rationale",
            type: "string",
          },
        ],
      ),

      // 4. Channel-specific content generation
      aiNode(
        "4",
        "AI: Generate channel-specific content",
        760,
        "You are a multi-channel content creator. Generate tailored content for each channel: a full email (subject + body HTML), a concise push notification (title + body, max 50 chars each), and an SMS message (max 160 chars). Each should convey the same core message but be optimized for its medium. Return JSON with email_subject, email_body_html, push_title, push_body, sms_text, and content_theme.",
        "Event: {journey_event_type}. Context: {event_context}. Urgency: {urgency_level}. Customer ID: {customer_external_id}.",
        [
          {
            field: "email_subject",
            as: "journey_email_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "journey_email_body",
            type: "string",
          },
          { field: "push_title", as: "journey_push_title", type: "string" },
          { field: "push_body", as: "journey_push_body", type: "string" },
          { field: "sms_text", as: "journey_sms_text", type: "string" },
          { field: "content_theme", as: "content_theme", type: "string" },
        ],
      ),

      // 5. Email touchpoint
      emailNode(
        "5",
        "Send primary email touchpoint",
        1000,
        "{customer_email}",
        "{journey_email_subject}",
        "{journey_email_body}",
      ),

      // 6. Wait + engagement check (delay)
      n("6", "delayAction", "Wait for email engagement window", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "{delay_after_primary}",
          delayUnit: "hours",
          description:
            "Waiting for primary channel engagement before escalating to secondary channel",
        },
      }),

      // 7. Engaged condition
      n("7", "condition", "Did customer engage with the email?", 1480, {
        conditionData: [
          {
            variable: "email_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Push notification fallback (False path)
      n(
        "8",
        "pushNotification",
        "Send push notification fallback",
        1720,
        {
          pushNotificationDetails: {
            title: "{journey_push_title}",
            body: "{journey_push_body}",
            targetAudience: "{customer_external_id}",
            data: '{"event_type":"{journey_event_type}","journey_step":"push_fallback"}',
          },
        },
        400,
      ),

      // 9. SMS final fallback
      n(
        "9",
        "sendSMS",
        "Send SMS final fallback",
        1960,
        {
          sendSMSDetails: {
            to: "{customer_phone}",
            message: "{journey_sms_text}",
          },
        },
        400,
      ),

      // 10. Journey attribution logging (Sheets)
      sheetsNode(
        "10",
        "Log journey attribution (Sheets)",
        2200,
        "CrossChannelJourneys",
        '[["{customer_external_id}","{journey_event_type}","{content_theme}","{primary_channel}","{secondary_channel}","{urgency_level}","{journey_rationale}","{delay_after_primary}h"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "10", "output_1"), // engaged -> log success (no fallback needed)
      e("7", "8", "output_2"), // not engaged -> push fallback
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 897. BRAZE EVENT-TRIGGERED CAMPAIGN (8 nodes)
  // ===================================================================

  "Braze Event-Triggered Campaign": {
    summary:
      "Deploys personalized campaigns in response to real-time user events. An event listener webhook evaluates context, qualification rules gate eligibility, AI generates personalized content, optimal channel is selected, Braze dispatches the campaign, performance is tracked in Sheets, and marketing ops is notified via Slack.",
    nodes: [
      trigger("Webhook: User event for campaign trigger", "webhook_app", {
        source: "Braze / App / Website",
        event: "user.event.fired",
        howItStarts:
          "A user performs a tracked event (product view, add-to-cart, feature usage, milestone reached) that qualifies them for a targeted Braze campaign with real-time personalized content",
        setup:
          "Configure Braze custom events or your app to POST user events to this workflow URL. Include external_user_id, event_name, event_properties, and timestamp.",
        inputFields: [],
      }),

      // 2. Event context evaluation
      aiNode(
        "2",
        "Extract and evaluate event context",
        280,
        "You are an event processing assistant. Extract the user external_id, event_name, event_properties (product_id, category, value, etc.), and timestamp from the webhook payload. Determine event_category (browse/purchase/engagement/milestone). Return JSON.",
        "Extract event data from: {webhook_payload}",
        [
          { field: "external_user_id", as: "event_user_id", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          {
            field: "event_properties",
            as: "event_properties_json",
            type: "string",
          },
          { field: "event_category", as: "event_category", type: "string" },
          { field: "event_value", as: "event_value", type: "string" },
          { field: "is_qualified", as: "is_qualified", type: "string" },
        ],
      ),

      // 3. Qualification rules condition
      n("3", "condition", "Does the event qualify for a campaign?", 520, {
        conditionData: [
          {
            variable: "is_qualified",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI personalized content
      aiNode(
        "4",
        "AI: Generate event-personalized content",
        760,
        "You are a real-time marketing content creator. Based on the user event, create personalized campaign content. Match the message to the event type (browse -> recommendation, purchase -> cross-sell, engagement -> reward, milestone -> celebration). Return JSON with campaign_subject, campaign_body_html, push_message, recommended_channel (email/push/in-app), and personalization_notes.",
        "Event: {event_name}. Category: {event_category}. Properties: {event_properties_json}. Value: {event_value}.",
        [
          {
            field: "campaign_subject",
            as: "event_campaign_subject",
            type: "string",
          },
          {
            field: "campaign_body_html",
            as: "event_campaign_body",
            type: "string",
          },
          { field: "push_message", as: "event_push_message", type: "string" },
          {
            field: "recommended_channel",
            as: "recommended_channel",
            type: "string",
          },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
        ],
      ),

      // 5. Optimal channel selection + Braze dispatch
      brazePostNode(
        "5",
        "Dispatch event-triggered campaign (Braze)",
        1000,
        "campaigns/trigger/send",
        [
          { key: "campaign_id", value: "{braze_event_campaign_id}" },
          {
            key: "recipients",
            value:
              '[{"external_user_id":"{event_user_id}","trigger_properties":{"subject":"{event_campaign_subject}","body":"{event_campaign_body}","channel":"{recommended_channel}","event_name":"{event_name}"}}]',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "event_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "campaign_dispatch_status",
            type: "string",
          },
        ],
      ),

      // 6. Send via recommended channel (email as primary)
      emailNode(
        "6",
        "Send event-triggered email",
        1240,
        "{event_user_email}",
        "{event_campaign_subject}",
        "{event_campaign_body}",
      ),

      // 7. Event campaign performance tracking (Sheets)
      sheetsNode(
        "7",
        "Track event campaign performance (Sheets)",
        1480,
        "EventCampaigns",
        '[["{event_name}","{event_category}","{event_user_id}","{recommended_channel}","{event_campaign_subject}","{event_dispatch_id}","{campaign_dispatch_status}","{personalization_notes}"]]',
      ),

      // 8. Marketing ops Slack notification
      slackNode(
        "8",
        "Notify marketing ops (Slack)",
        1720,
        ":zap: *Event-Triggered Campaign Deployed*\n\n*Event:* {event_name} ({event_category})\n*User:* {event_user_id}\n*Channel:* {recommended_channel}\n*Subject:* {event_campaign_subject}\n*Status:* {campaign_dispatch_status}\n*Dispatch ID:* {event_dispatch_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // qualified -> generate content
      e("3", "7", "output_2"), // not qualified -> log and skip
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 898. BRAZE IN-APP MESSAGE TRIGGER (7 nodes)
  // ===================================================================

  "Braze In-App Message Trigger": {
    summary:
      "Deploys contextual in-app messages based on user events. A user event trigger checks display eligibility via runCode, AI generates contextual message content, Braze deploys the in-app message, interaction metrics are tracked, AI generates optimization suggestions, and a performance report is logged to Sheets.",
    nodes: [
      trigger("Webhook: User event for in-app message", "webhook_app", {
        source: "App / Braze SDK",
        event: "user.inapp.eligible",
        howItStarts:
          "A user performs an in-app action (screen view, feature tap, milestone, etc.) that qualifies them for a contextual in-app message delivered through Braze",
        setup:
          "Configure the Braze SDK or app backend to POST user events to this workflow URL when in-app message conditions are met. Include user_id, screen_name, action_type, and session_context.",
        inputFields: [],
      }),

      // 2. Display eligibility check (runCode)
      n("2", "runCode", "Check display eligibility and frequency caps", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if user is eligible for in-app message display
const payload = JSON.parse(vars.webhook_payload || "{}");
const userId = payload.external_user_id || payload.user_id || "";
const screenName = payload.screen_name || payload.current_screen || "";
const actionType = payload.action_type || payload.event || "";
const sessionDuration = parseInt(payload.session_duration_seconds || "0");
const messagesShownThisSession = parseInt(payload.messages_shown || "0");
const lastMessageTimestamp = payload.last_message_shown_at || "";

const maxPerSession = 3;
const minSecondsBetween = 120;
const minSessionDuration = 10;

const belowCap = messagesShownThisSession < maxPerSession;
const longEnoughSession = sessionDuration >= minSessionDuration;
const timeSinceLast = lastMessageTimestamp ? (Date.now() - new Date(lastMessageTimestamp).getTime()) / 1000 : Infinity;
const enoughTimePassed = timeSinceLast >= minSecondsBetween;
const isEligible = belowCap && longEnoughSession && enoughTimePassed;

return {
  user_id: userId,
  screen_name: screenName,
  action_type: actionType,
  is_eligible: isEligible ? "yes" : "no",
  messages_shown: messagesShownThisSession,
  session_seconds: sessionDuration,
  eligibility_reason: !belowCap ? "frequency cap reached" : !longEnoughSession ? "session too short" : !enoughTimePassed ? "too soon after last message" : "all checks passed"
};`,
          outputVariables: [
            { name: "user_id", type: "string" },
            { name: "screen_name", type: "string" },
            { name: "action_type", type: "string" },
            { name: "is_eligible", type: "string" },
            { name: "messages_shown", type: "number" },
            { name: "session_seconds", type: "number" },
            { name: "eligibility_reason", type: "string" },
          ],
        },
      }),

      // 3. AI contextual message content
      aiNode(
        "3",
        "AI: Generate contextual in-app message",
        520,
        "You are an in-app messaging specialist. Based on the user's current screen, action, and session context, create a compelling in-app message. Choose the best format (modal, slideup, full-screen). Return JSON with message_type (modal/slideup/full), header_text (max 30 chars), body_text (max 100 chars), cta_text (max 20 chars), image_url (suggest a relevant image category), dismiss_type (auto_dismiss/user_dismiss), and display_duration_seconds.",
        "Screen: {screen_name}. Action: {action_type}. Session duration: {session_seconds}s. Messages shown: {messages_shown}.",
        [
          { field: "message_type", as: "iam_message_type", type: "string" },
          { field: "header_text", as: "iam_header", type: "string" },
          { field: "body_text", as: "iam_body", type: "string" },
          { field: "cta_text", as: "iam_cta", type: "string" },
          {
            field: "display_duration_seconds",
            as: "iam_duration",
            type: "number",
          },
        ],
      ),

      // 4. Braze in-app deployment
      brazePostNode(
        "4",
        "Deploy in-app message (Braze)",
        760,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{user_id}"]' },
          {
            key: "messages",
            value:
              '{"in_app_message":{"type":"{iam_message_type}","header":"{iam_header}","body":"{iam_body}","button_text":"{iam_cta}","duration":{iam_duration}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "iam_dispatch_id",
            type: "string",
          },
          { name: "message", customName: "iam_deploy_status", type: "string" },
        ],
      ),

      // 5. Interaction metrics tracking (runCode)
      n("5", "runCode", "Track interaction metrics", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track in-app message interaction metrics
const impressionLogged = vars.iam_deploy_status === "success";
const timestamp = new Date().toISOString();

return {
  impression_logged: impressionLogged ? "yes" : "no",
  message_type_used: vars.iam_message_type || "unknown",
  screen_context: vars.screen_name || "unknown",
  action_context: vars.action_type || "unknown",
  tracking_timestamp: timestamp,
  metrics_summary: "IAM " + (vars.iam_message_type || "unknown") + " on " + (vars.screen_name || "unknown") + " at " + timestamp
};`,
          outputVariables: [
            { name: "impression_logged", type: "string" },
            { name: "message_type_used", type: "string" },
            { name: "screen_context", type: "string" },
            { name: "action_context", type: "string" },
            { name: "tracking_timestamp", type: "string" },
            { name: "metrics_summary", type: "string" },
          ],
        },
      }),

      // 6. AI optimization suggestions
      aiNode(
        "6",
        "AI: Generate optimization suggestions",
        1240,
        "You are an in-app messaging optimization expert. Based on the deployment context and metrics, suggest improvements for future in-app messages on this screen/action combination. Return JSON with optimization_suggestions (array of strings), recommended_test (an A/B test idea), and engagement_prediction (low/medium/high).",
        "Message type: {message_type_used}. Screen: {screen_context}. Action: {action_context}. Header: {iam_header}. Body: {iam_body}. Session duration: {session_seconds}s.",
        [
          {
            field: "optimization_suggestions",
            as: "optimization_suggestions",
            type: "string",
          },
          {
            field: "recommended_test",
            as: "recommended_ab_test",
            type: "string",
          },
          {
            field: "engagement_prediction",
            as: "engagement_prediction",
            type: "string",
          },
        ],
      ),

      // 7. Performance report (Sheets)
      sheetsNode(
        "7",
        "Log in-app message performance (Sheets)",
        1480,
        "InAppMessages",
        '[["{tracking_timestamp}","{user_id}","{screen_context}","{action_context}","{message_type_used}","{iam_header}","{engagement_prediction}","{optimization_suggestions}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 899. BRAZE ONBOARDING CANVAS (9 nodes)
  // ===================================================================

  "Braze Onboarding Canvas": {
    summary:
      "Guides new users through a personalized onboarding journey. A signup trigger fetches user context, AI determines the optimal onboarding path, Braze canvas enrollment is initiated, a Day 0 welcome email is sent, a delay monitors milestones, a condition checks milestone completion, multi-channel nudges deploy via push and in-app for incomplete milestones, and an onboarding completion report is logged to Sheets.",
    nodes: [
      trigger("Webhook: New user signup", "webhook_app", {
        source: "App / Website / Auth0",
        event: "user.created",
        howItStarts:
          "A new user completes registration, triggering an intelligent onboarding journey that adapts to their behavior and guides them to key activation milestones through email, push, and in-app messaging via Braze",
        setup:
          "Configure your auth system or app backend to POST new user events to this workflow URL. Include user_id, email, signup_source, and any initial profile data (plan, industry, role).",
        inputFields: [],
      }),

      // 2. User context fetch
      aiNode(
        "2",
        "Extract new user context",
        280,
        "You are a user data extraction assistant. Extract the new user's external_id, email, name, signup_source (web/mobile/referral), plan_type (free/trial/paid), industry, role, and any stated goals from the webhook payload. Return JSON.",
        "Extract new user data from: {webhook_payload}",
        [
          { field: "external_id", as: "new_user_id", type: "string" },
          { field: "email", as: "new_user_email", type: "string" },
          { field: "name", as: "new_user_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "plan_type", as: "plan_type", type: "string" },
          { field: "industry", as: "user_industry", type: "string" },
        ],
      ),

      // 3. AI onboarding path determination
      aiNode(
        "3",
        "AI: Determine optimal onboarding path",
        520,
        "You are an onboarding strategist. Based on the user's profile, plan, signup source, and industry, determine the optimal onboarding path. Define key milestones (3-5), suggested timeline, and messaging tone. Return JSON with onboarding_path (name), milestones (array with name, description, target_day), welcome_message_subject, welcome_message_body_html, onboarding_duration_days, and personalization_strategy.",
        "User: {new_user_name}. Plan: {plan_type}. Source: {signup_source}. Industry: {user_industry}.",
        [
          { field: "onboarding_path", as: "onboarding_path", type: "string" },
          { field: "milestones", as: "milestones_json", type: "string" },
          {
            field: "welcome_message_subject",
            as: "welcome_subject",
            type: "string",
          },
          {
            field: "welcome_message_body_html",
            as: "welcome_body",
            type: "string",
          },
          {
            field: "onboarding_duration_days",
            as: "onboarding_days",
            type: "number",
          },
          {
            field: "personalization_strategy",
            as: "personalization_strategy",
            type: "string",
          },
        ],
      ),

      // 4. Braze canvas enrollment
      brazePostNode(
        "4",
        "Enroll user in Braze onboarding canvas",
        760,
        "canvas/trigger/send",
        [
          { key: "canvas_id", value: "{braze_onboarding_canvas_id}" },
          {
            key: "recipients",
            value:
              '[{"external_user_id":"{new_user_id}","canvas_entry_properties":{"path":"{onboarding_path}","milestones":"{milestones_json}","plan":"{plan_type}"}}]',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "onboarding_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "canvas_enrollment_status",
            type: "string",
          },
        ],
      ),

      // 5. Welcome email (Day 0)
      emailNode(
        "5",
        "Send Day 0 welcome email",
        1000,
        "{new_user_email}",
        "{welcome_subject}",
        "{welcome_body}",
      ),

      // 6. Milestone monitoring (delay)
      n("6", "delayAction", "Wait for initial milestone window", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "72",
          delayUnit: "hours",
          description:
            "Waiting 3 days to check if user has completed initial onboarding milestones before sending nudge messages",
        },
      }),

      // 7. Milestone condition
      n("7", "condition", "Has user completed first milestone?", 1480, {
        conditionData: [
          {
            variable: "milestone_1_completed",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Multi-channel nudges (push + in-app)
      n(
        "8",
        "pushNotification",
        "Send milestone nudge (push notification)",
        1720,
        {
          pushNotificationDetails: {
            title: "You're almost there, {new_user_name}!",
            body: "Complete your first milestone to unlock the full experience. Tap to continue.",
            targetAudience: "{new_user_id}",
            data: '{"onboarding_path":"{onboarding_path}","action":"resume_onboarding"}',
          },
        },
        400,
      ),

      // 9. Onboarding completion report (Sheets)
      sheetsNode(
        "9",
        "Log onboarding progress (Sheets)",
        1960,
        "OnboardingJourneys",
        '[["{new_user_id}","{new_user_email}","{signup_source}","{plan_type}","{onboarding_path}","{onboarding_days}","{canvas_enrollment_status}","{personalization_strategy}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "9", "output_1"), // milestone completed -> log success
      e("7", "8", "output_2"), // not completed -> send nudge
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 900. BRAZE PUSH NOTIFICATION CAMPAIGN (8 nodes)
  // ===================================================================

  "Braze Push Notification Campaign": {
    summary:
      "Manages end-to-end push notification campaigns through Braze. A campaign activation webhook defines the target audience, AI generates push content variants, rich media is configured via runCode, send time is optimized, Braze deploys the push campaign, delivery and engagement are monitored, and campaign results are logged to Sheets.",
    nodes: [
      trigger("Webhook: Push campaign activation request", "webhook_app", {
        source: "Marketing Portal / Campaign Manager",
        event: "campaign.push.activated",
        howItStarts:
          "A marketing team member activates a push notification campaign with target audience, message objectives, and scheduling preferences",
        setup:
          "Configure your campaign management tool to POST push campaign requests to this workflow URL. Include campaign name, objective, target segment, and scheduling preferences.",
        inputFields: [],
      }),

      // 2. Braze target audience definition
      aiNode(
        "2",
        "Extract campaign config & define audience",
        280,
        "You are a campaign operations assistant. Extract the campaign name, objective, target segment description, urgency, and scheduling preference from the webhook payload. Determine the appropriate Braze segment ID based on the target description. Return JSON.",
        "Extract push campaign config from: {webhook_payload}",
        [
          { field: "campaign_name", as: "push_campaign_name", type: "string" },
          { field: "objective", as: "push_objective", type: "string" },
          {
            field: "target_segment",
            as: "push_target_segment",
            type: "string",
          },
          { field: "urgency", as: "push_urgency", type: "string" },
          {
            field: "schedule_preference",
            as: "schedule_preference",
            type: "string",
          },
          {
            field: "estimated_audience_size",
            as: "estimated_audience",
            type: "number",
          },
        ],
      ),

      // 3. AI push content variants
      aiNode(
        "3",
        "AI: Generate push notification content variants",
        520,
        "You are a push notification copywriter. Create 3 content variants for A/B testing. Each variant should have a title (max 40 chars), body (max 120 chars), and a distinct approach (urgency, curiosity, value). Return JSON with variants (array of 3 objects with title, body, approach), recommended_variant, and creative_notes.",
        "Campaign: {push_campaign_name}. Objective: {push_objective}. Urgency: {push_urgency}. Audience: {push_target_segment}.",
        [
          { field: "variants", as: "push_variants_json", type: "string" },
          {
            field: "recommended_variant",
            as: "recommended_variant",
            type: "string",
          },
          {
            field: "creative_notes",
            as: "push_creative_notes",
            type: "string",
          },
        ],
      ),

      // 4. Rich media configuration (runCode)
      n("4", "runCode", "Configure rich media and action buttons", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Configure rich media assets and action buttons for push notification
const variants = JSON.parse(vars.push_variants_json || "[]");
const objective = vars.push_objective || "engagement";
const urgency = vars.push_urgency || "medium";

const actionButtons = [];
if (objective.includes("sale") || objective.includes("promo")) {
  actionButtons.push({ id: "shop_now", text: "Shop Now", action: "deep_link", uri: "app://shop" });
  actionButtons.push({ id: "view_deal", text: "View Deal", action: "deep_link", uri: "app://deals" });
} else if (objective.includes("content") || objective.includes("engagement")) {
  actionButtons.push({ id: "read_more", text: "Read More", action: "deep_link", uri: "app://content" });
  actionButtons.push({ id: "later", text: "Remind Later", action: "dismiss" });
} else {
  actionButtons.push({ id: "open_app", text: "Open App", action: "deep_link", uri: "app://home" });
}

const richMedia = {
  image_url: "{push_hero_image_url}",
  badge_count: urgency === "high" ? 1 : 0,
  sound: urgency === "high" ? "alert.aiff" : "default",
  category: objective
};

const configuredVariants = variants.map((v, i) => ({
  ...v,
  buttons: actionButtons,
  rich_media: richMedia,
  variant_id: "variant_" + (i + 1)
}));

return {
  configured_variants_json: JSON.stringify(configuredVariants),
  action_button_count: actionButtons.length,
  has_rich_media: "yes",
  sound_setting: richMedia.sound,
  push_category: richMedia.category,
  config_summary: configuredVariants.length + " variants with " + actionButtons.length + " action buttons configured"
};`,
          outputVariables: [
            { name: "configured_variants_json", type: "string" },
            { name: "action_button_count", type: "number" },
            { name: "has_rich_media", type: "string" },
            { name: "sound_setting", type: "string" },
            { name: "push_category", type: "string" },
            { name: "config_summary", type: "string" },
          ],
        },
      }),

      // 5. Send time optimization (AI)
      aiNode(
        "5",
        "AI: Determine optimal send time",
        1000,
        "You are a send-time optimization engine. Based on the audience segment, urgency, and scheduling preference, determine the optimal send time and timezone strategy. Return JSON with send_time_utc, timezone_strategy (single/local_time), estimated_open_rate, and timing_rationale.",
        "Audience: {push_target_segment}. Urgency: {push_urgency}. Preference: {schedule_preference}. Estimated size: {estimated_audience}.",
        [
          { field: "send_time_utc", as: "optimal_send_time", type: "string" },
          {
            field: "timezone_strategy",
            as: "timezone_strategy",
            type: "string",
          },
          {
            field: "estimated_open_rate",
            as: "estimated_open_rate",
            type: "string",
          },
          { field: "timing_rationale", as: "timing_rationale", type: "string" },
        ],
      ),

      // 6. Braze push deployment
      brazePostNode(
        "6",
        "Deploy push campaign (Braze)",
        1240,
        "campaigns/trigger/send",
        [
          { key: "campaign_id", value: "{braze_push_campaign_id}" },
          { key: "broadcast", value: "true" },
          {
            key: "trigger_properties",
            value:
              '{"variants":"{configured_variants_json}","send_time":"{optimal_send_time}","timezone":"{timezone_strategy}"}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "push_dispatch_id",
            type: "string",
          },
          { name: "message", customName: "push_deploy_status", type: "string" },
        ],
      ),

      // 7. Delivery + engagement monitoring (delay then check)
      n("7", "delayAction", "Monitor delivery and engagement", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "4",
          delayUnit: "hours",
          description:
            "Waiting 4 hours post-deployment to gather delivery and engagement metrics",
        },
      }),

      // 8. Campaign results report (Sheets)
      sheetsNode(
        "8",
        "Log push campaign results (Sheets)",
        1720,
        "PushCampaigns",
        '[["{push_campaign_name}","{push_objective}","{push_target_segment}","{estimated_audience}","{optimal_send_time}","{timezone_strategy}","{push_deploy_status}","{estimated_open_rate}","{config_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 901. BRAZE RE-ENGAGEMENT CAMPAIGN (8 nodes)
  // ===================================================================

  "Braze Re-Engagement Campaign": {
    summary:
      "Detects dormant users and deploys multi-touch re-engagement campaigns through Braze. A dormancy schedule detects inactive users, profiles are fetched, segmentation by value and duration is calculated via runCode, AI generates win-back content, Braze test variants are configured, a multi-touch canvas is deployed, re-engagement signals are monitored, and results are logged to Sheets.",
    nodes: [
      trigger("Schedule: Dormancy detection scan", "schedule", {
        schedule: "0 6 * * 1 (weekly on Monday at 6 AM)",
        howItStarts:
          "A weekly scheduled job scans for dormant users who have not engaged in a configurable time window and deploys tiered re-engagement campaigns through Braze",
        setup:
          "Configure Braze API credentials. Define dormancy thresholds (e.g., 30/60/90 days inactive). Set up re-engagement canvas and campaign IDs in Braze. Configure win-back offer tiers.",
        inputFields: [],
      }),

      // 2. Dormant profile fetch
      brazePostNode(
        "2",
        "Fetch dormant user profiles (Braze)",
        280,
        "users/export/segment",
        [
          { key: "segment_id", value: "{braze_dormant_users_segment_id}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","last_active","total_revenue","purchase_count","custom_attributes"]',
          },
        ],
        [
          { name: "users", customName: "dormant_users_json", type: "string" },
          { name: "message", customName: "export_status", type: "string" },
        ],
      ),

      // 3. Value/duration segmentation (runCode)
      n("3", "runCode", "Segment by value and dormancy duration", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment dormant users by lifetime value and dormancy duration
const users = JSON.parse(vars.dormant_users_json || "[]");
const now = Date.now();
const segments = { high_value_recent: [], high_value_long: [], low_value_recent: [], low_value_long: [] };
const valueThreshold = 100;
const recentDays = 60;

for (const user of users) {
  const daysDormant = Math.round((now - new Date(user.last_active || 0).getTime()) / 86400000);
  const ltv = user.total_revenue || 0;
  const isHighValue = ltv >= valueThreshold;
  const isRecent = daysDormant <= recentDays;
  const segment = (isHighValue ? "high_value_" : "low_value_") + (isRecent ? "recent" : "long");
  segments[segment].push({
    external_id: user.external_id,
    email: user.email,
    days_dormant: daysDormant,
    lifetime_value: ltv,
    purchase_count: user.purchase_count || 0,
    segment: segment
  });
}

const totalDormant = users.length;
const priorityUsers = [...segments.high_value_recent, ...segments.high_value_long].slice(0, 100);

return {
  total_dormant: totalDormant,
  high_value_recent_count: segments.high_value_recent.length,
  high_value_long_count: segments.high_value_long.length,
  low_value_recent_count: segments.low_value_recent.length,
  low_value_long_count: segments.low_value_long.length,
  priority_users_json: JSON.stringify(priorityUsers),
  segmentation_summary: segments.high_value_recent.length + " high-value recent, " + segments.high_value_long.length + " high-value long, " + segments.low_value_recent.length + " low-value recent, " + segments.low_value_long.length + " low-value long",
  scan_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "total_dormant", type: "number" },
            { name: "high_value_recent_count", type: "number" },
            { name: "high_value_long_count", type: "number" },
            { name: "low_value_recent_count", type: "number" },
            { name: "low_value_long_count", type: "number" },
            { name: "priority_users_json", type: "string" },
            { name: "segmentation_summary", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 4. AI win-back content
      aiNode(
        "4",
        "AI: Generate tiered win-back content",
        760,
        "You are a re-engagement marketing specialist. Create win-back content for each dormant segment. High-value users get exclusive offers, low-value get value reminders. Return JSON with high_value_subject, high_value_body_html, high_value_offer, low_value_subject, low_value_body_html, low_value_offer, push_message, and content_strategy.",
        "Segments: {segmentation_summary}. Priority users sample: {priority_users_json}.",
        [
          { field: "high_value_subject", as: "hv_subject", type: "string" },
          { field: "high_value_body_html", as: "hv_body", type: "string" },
          { field: "high_value_offer", as: "hv_offer", type: "string" },
          { field: "low_value_subject", as: "lv_subject", type: "string" },
          { field: "low_value_body_html", as: "lv_body", type: "string" },
          { field: "push_message", as: "winback_push_message", type: "string" },
          { field: "content_strategy", as: "content_strategy", type: "string" },
        ],
      ),

      // 5. Braze test variant configuration
      brazePostNode(
        "5",
        "Configure Braze re-engagement test variants",
        1000,
        "campaigns/trigger/send",
        [
          { key: "campaign_id", value: "{braze_reengagement_campaign_id}" },
          { key: "recipients", value: "{priority_users_json}" },
          {
            key: "trigger_properties",
            value:
              '{"hv_subject":"{hv_subject}","hv_body":"{hv_body}","hv_offer":"{hv_offer}","lv_subject":"{lv_subject}","lv_body":"{lv_body}"}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "reengagement_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "reengagement_status",
            type: "string",
          },
        ],
      ),

      // 6. Multi-touch canvas deployment
      brazePostNode(
        "6",
        "Deploy multi-touch re-engagement canvas",
        1240,
        "canvas/trigger/send",
        [
          { key: "canvas_id", value: "{braze_reengagement_canvas_id}" },
          { key: "recipients", value: "{priority_users_json}" },
          {
            key: "canvas_entry_properties",
            value:
              '{"strategy":"{content_strategy}","push_message":"{winback_push_message}"}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "canvas_dispatch_id",
            type: "string",
          },
          {
            name: "message",
            customName: "canvas_deploy_status",
            type: "string",
          },
        ],
      ),

      // 7. Re-engagement signal monitoring (delay)
      n("7", "delayAction", "Monitor re-engagement signals", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "7",
          delayUnit: "days",
          description:
            "Waiting 7 days to measure re-engagement campaign effectiveness and user return signals",
        },
      }),

      // 8. Results report (Sheets)
      sheetsNode(
        "8",
        "Log re-engagement results (Sheets)",
        1720,
        "ReEngagement",
        '[["{scan_date}","{total_dormant}","{segmentation_summary}","{content_strategy}","{reengagement_status}","{canvas_deploy_status}","{hv_offer}","{reengagement_dispatch_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 902. BRAZE USER SEGMENT SYNC (8 nodes)
  // ===================================================================

  "Braze User Segment Sync": {
    summary:
      "Synchronizes user segments between Braze and a data warehouse. A scheduled sync exports Braze segments, queries the warehouse for its segment definitions, runCode calculates differences, conflicts are resolved, Braze profiles are updated, warehouse writeback is performed, and a sync audit report is logged to Sheets.",
    nodes: [
      trigger("Schedule: Bi-directional segment sync", "schedule", {
        schedule: "0 3 * * * (daily at 3 AM)",
        howItStarts:
          "A daily scheduled job synchronizes user segment membership between Braze and the data warehouse to ensure consistent targeting across all marketing channels and analytics platforms",
        setup:
          "Configure Braze API credentials and data warehouse connection (BigQuery/Snowflake). Define segment mapping table. Set conflict resolution rules (Braze-wins, warehouse-wins, most-recent).",
        inputFields: [],
      }),

      // 2. Braze segment export
      brazePostNode(
        "2",
        "Export Braze user segments",
        280,
        "users/export/segment",
        [
          { key: "segment_id", value: "{braze_sync_segment_id}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","custom_attributes.segment_tags","custom_attributes.last_segment_update"]',
          },
        ],
        [
          { name: "users", customName: "braze_users_json", type: "string" },
          {
            name: "message",
            customName: "braze_export_status",
            type: "string",
          },
        ],
      ),

      // 3. Data warehouse segment query
      n("3", "hitApi", "Query warehouse segment definitions (BigQuery)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://bigquery.googleapis.com/bigquery/v2/projects/{gcp_project_id}/queries",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT user_id, email, segment_tags, last_updated FROM `{dataset}.user_segments` WHERE last_updated >= TIMESTAMP_SUB(CURRENT_TIMESTAMP(), INTERVAL 24 HOUR)",
            },
            { key: "useLegacySql", value: "false" },
          ],
          variables: [
            {
              name: "rows",
              customName: "warehouse_users_json",
              type: "string",
            },
            {
              name: "totalRows",
              customName: "warehouse_row_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Segment difference calculation (runCode)
      n("4", "runCode", "Calculate segment differences", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate differences between Braze and warehouse segments
const brazeUsers = JSON.parse(vars.braze_users_json || "[]");
const warehouseUsers = JSON.parse(vars.warehouse_users_json || "[]");
const conflictRule = vars.conflict_resolution || "most_recent";

const brazeMap = {};
for (const u of brazeUsers) {
  const attrs = u.custom_attributes || {};
  brazeMap[u.external_id] = { segments: attrs.segment_tags || "", lastUpdate: attrs.last_segment_update || "" };
}

const warehouseMap = {};
for (const row of warehouseUsers) {
  warehouseMap[row.user_id || row.f_0] = { segments: row.segment_tags || row.f_2 || "", lastUpdate: row.last_updated || row.f_3 || "" };
}

const toUpdateBraze = [];
const toUpdateWarehouse = [];
const conflicts = [];
const allIds = new Set([...Object.keys(brazeMap), ...Object.keys(warehouseMap)]);

for (const id of allIds) {
  const braze = brazeMap[id];
  const wh = warehouseMap[id];
  if (braze && wh && braze.segments !== wh.segments) {
    const brazeNewer = braze.lastUpdate > wh.lastUpdate;
    if (conflictRule === "braze_wins" || (conflictRule === "most_recent" && brazeNewer)) {
      toUpdateWarehouse.push({ user_id: id, segments: braze.segments });
    } else {
      toUpdateBraze.push({ external_id: id, segments: wh.segments });
    }
    conflicts.push({ user_id: id, braze: braze.segments, warehouse: wh.segments });
  } else if (!braze && wh) {
    toUpdateBraze.push({ external_id: id, segments: wh.segments });
  } else if (braze && !wh) {
    toUpdateWarehouse.push({ user_id: id, segments: braze.segments });
  }
}

return {
  braze_update_count: toUpdateBraze.length,
  warehouse_update_count: toUpdateWarehouse.length,
  conflict_count: conflicts.length,
  braze_updates_json: JSON.stringify(toUpdateBraze.slice(0, 100)),
  warehouse_updates_json: JSON.stringify(toUpdateWarehouse.slice(0, 100)),
  total_users_compared: allIds.size,
  sync_summary: toUpdateBraze.length + " Braze updates, " + toUpdateWarehouse.length + " warehouse updates, " + conflicts.length + " conflicts resolved (" + conflictRule + ")",
  sync_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "braze_update_count", type: "number" },
            { name: "warehouse_update_count", type: "number" },
            { name: "conflict_count", type: "number" },
            { name: "braze_updates_json", type: "string" },
            { name: "warehouse_updates_json", type: "string" },
            { name: "total_users_compared", type: "number" },
            { name: "sync_summary", type: "string" },
            { name: "sync_timestamp", type: "string" },
          ],
        },
      }),

      // 5. Conflict resolution (AI for complex cases)
      aiNode(
        "5",
        "AI: Review and resolve complex conflicts",
        1000,
        "You are a data integrity specialist. Review the segment sync conflicts and provide resolution recommendations for any edge cases. Validate that the automated resolution is correct. Return JSON with resolution_approved (yes/no), edge_cases (array of any concerning conflicts), resolution_notes, and data_quality_score (1-10).",
        "Sync summary: {sync_summary}. Conflict count: {conflict_count}. Braze updates: {braze_update_count}. Warehouse updates: {warehouse_update_count}. Total compared: {total_users_compared}.",
        [
          {
            field: "resolution_approved",
            as: "resolution_approved",
            type: "string",
          },
          { field: "edge_cases", as: "edge_cases_json", type: "string" },
          { field: "resolution_notes", as: "resolution_notes", type: "string" },
          {
            field: "data_quality_score",
            as: "data_quality_score",
            type: "number",
          },
        ],
      ),

      // 6. Braze profile update
      brazePostNode(
        "6",
        "Update Braze user profiles",
        1240,
        "users/track",
        [{ key: "attributes", value: "{braze_updates_json}" }],
        [
          {
            name: "message",
            customName: "braze_update_status",
            type: "string",
          },
          {
            name: "attributes_processed",
            customName: "braze_profiles_updated",
            type: "number",
          },
        ],
      ),

      // 7. Warehouse writeback
      n(
        "7",
        "hitApi",
        "Write back updates to data warehouse (BigQuery)",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://bigquery.googleapis.com/bigquery/v2/projects/{gcp_project_id}/queries",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  "MERGE `{dataset}.user_segments` AS target USING UNNEST(JSON_EXTRACT_ARRAY('{warehouse_updates_json}')) AS source ON target.user_id = JSON_VALUE(source, '$.user_id') WHEN MATCHED THEN UPDATE SET segment_tags = JSON_VALUE(source, '$.segments'), last_updated = CURRENT_TIMESTAMP()",
              },
              { key: "useLegacySql", value: "false" },
            ],
            variables: [
              {
                name: "numDmlAffectedRows",
                customName: "warehouse_rows_updated",
                type: "number",
              },
              {
                name: "jobComplete",
                customName: "warehouse_write_status",
                type: "boolean",
              },
            ],
          },
        },
      ),

      // 8. Sync audit report (Sheets)
      sheetsNode(
        "8",
        "Log segment sync audit (Sheets)",
        1720,
        "SegmentSyncAudit",
        '[["{sync_timestamp}","{total_users_compared}","{braze_update_count}","{warehouse_update_count}","{conflict_count}","{data_quality_score}","{sync_summary}","{resolution_notes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 903. BROWSER-PUSH WELCOME AFTER PERMISSION (7 nodes)
  // ===================================================================

  "Browser-Push Welcome After Permission": {
    summary:
      "Delivers a timed welcome push notification series after browser push permission is granted. A permission trigger fetches visitor context, an immediate welcome push is sent via OneSignal, a Day-1 value push follows after a delay, a Day-3 exclusive push follows another delay, a condition checks engagement, and the series completion is logged to Sheets.",
    nodes: [
      trigger("Webhook: Browser push permission granted", "webhook_app", {
        source: "OneSignal / Website",
        event: "subscription.created",
        howItStarts:
          "A visitor grants browser push notification permission, triggering a timed welcome series that delivers immediate value and builds engagement habit through OneSignal web push",
        setup:
          "Configure OneSignal webhook or your website to POST push permission events to this workflow URL when a user subscribes. Include player_id, browser, device_type, and referring page.",
        inputFields: [],
      }),

      // 2. Visitor context fetch
      aiNode(
        "2",
        "Extract visitor context from push subscription",
        280,
        "You are a data extraction assistant. Extract the OneSignal player_id, browser type, device_type (desktop/mobile), referring_page_url, subscription_timestamp, and any user tags from the webhook payload. Return JSON.",
        "Extract push subscription data from: {webhook_payload}",
        [
          { field: "player_id", as: "onesignal_player_id", type: "string" },
          { field: "browser", as: "visitor_browser", type: "string" },
          { field: "device_type", as: "visitor_device", type: "string" },
          { field: "referring_page", as: "referring_page", type: "string" },
          {
            field: "subscription_timestamp",
            as: "subscription_time",
            type: "string",
          },
        ],
      ),

      // 3. Immediate welcome push (OneSignal)
      n("3", "hitApi", "Send immediate welcome push (OneSignal)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://onesignal.com/api/v1/notifications",
          headers: [
            { key: "Authorization", value: "Basic {onesignal_rest_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "app_id", value: "{onesignal_app_id}" },
            { key: "include_player_ids", value: '["{onesignal_player_id}"]' },
            { key: "headings", value: '{"en":"Welcome! You\'re all set."}' },
            {
              key: "contents",
              value:
                '{"en":"Thanks for subscribing! You\'ll get exclusive deals and content first. Here\'s a welcome treat just for you."}',
            },
            { key: "url", value: "{welcome_offer_url}" },
            { key: "chrome_web_image", value: "{welcome_push_image_url}" },
          ],
          variables: [
            { name: "id", customName: "welcome_push_id", type: "string" },
            {
              name: "recipients",
              customName: "welcome_push_recipients",
              type: "number",
            },
          ],
        },
      }),

      // 4. Day-1 value push (delay)
      n("4", "delayAction", "Wait 1 day for value push", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "24",
          delayUnit: "hours",
          description:
            "Waiting 24 hours before sending the Day-1 value push notification to maintain engagement without overwhelming the new subscriber",
        },
      }),

      // 5. Day-3 exclusive push (delay + send)
      n(
        "5",
        "hitApi",
        "Send Day-1 value push + schedule Day-3 (OneSignal)",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://onesignal.com/api/v1/notifications",
            headers: [
              { key: "Authorization", value: "Basic {onesignal_rest_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "app_id", value: "{onesignal_app_id}" },
              { key: "include_player_ids", value: '["{onesignal_player_id}"]' },
              { key: "headings", value: '{"en":"Your daily insider tip"}' },
              {
                key: "contents",
                value:
                  "{\"en\":\"Here's today's top pick just for subscribers. Don't miss out on what's trending right now.\"}",
              },
              { key: "url", value: "{daily_value_content_url}" },
            ],
            variables: [
              { name: "id", customName: "day1_push_id", type: "string" },
              {
                name: "recipients",
                customName: "day1_push_recipients",
                type: "number",
              },
            ],
          },
        },
      ),

      // 6. Engaged condition (after Day-3 delay)
      n("6", "condition", "Has subscriber engaged with any push?", 1240, {
        conditionData: [
          {
            variable: "welcome_push_recipients",
            value: "1",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Series completion logging (Sheets)
      sheetsNode(
        "7",
        "Log push welcome series completion (Sheets)",
        1480,
        "PushWelcomeSeries",
        '[["{subscription_time}","{onesignal_player_id}","{visitor_browser}","{visitor_device}","{referring_page}","{welcome_push_id}","{day1_push_id}","series_complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // engaged -> log completion
      e("6", "7", "output_2"), // not engaged -> still log
    ],
  },

  // ===================================================================
  // 904. BUDGET PACING ALERT (7 nodes)
  // ===================================================================

  "Budget Pacing Alert": {
    summary:
      "Monitors ad spend pacing multiple times daily and alerts on over/under-pacing. A multi-daily schedule fetches Google Ads spend data, pacing metrics are calculated via runCode, a condition evaluates pacing status, AI analyzes pacing deviations, a Slack alert notifies the team, and a pacing dashboard is logged to Sheets.",
    nodes: [
      trigger("Schedule: Multi-daily budget pacing check", "schedule", {
        schedule: "0 10,14,18 * * 1-5 (10 AM, 2 PM, 6 PM weekdays)",
        howItStarts:
          "A multi-daily scheduled check monitors Google Ads spend against daily and monthly budgets to detect over-pacing or under-pacing before it impacts campaign performance",
        setup:
          "Configure Google Ads API credentials. Set daily and monthly budget targets per campaign. Define pacing thresholds for alerts (e.g., +/-15% of target pace).",
        inputFields: [],
      }),

      // 2. Google Ads spend fetch
      n("2", "hitApi", "Fetch current spend data (Google Ads)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, campaign.budget_amount_micros, metrics.cost_micros, segments.date FROM campaign WHERE segments.date DURING THIS_MONTH ORDER BY campaign.name",
            },
          ],
          variables: [
            { name: "results", customName: "spend_data_json", type: "string" },
            {
              name: "results.length",
              customName: "campaign_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Pacing metrics calculation (runCode)
      n("3", "runCode", "Calculate pacing metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate budget pacing metrics for each campaign
const results = JSON.parse(vars.spend_data_json || "[]");
const now = new Date();
const dayOfMonth = now.getDate();
const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
const monthProgress = dayOfMonth / daysInMonth;
const hourOfDay = now.getHours();
const dayProgress = hourOfDay / 24;

const campaignPacing = {};
for (const row of results) {
  const name = row.campaign_name || "Unknown";
  if (!campaignPacing[name]) {
    campaignPacing[name] = { budget: (row.campaign_budget_amount_micros || 0) / 1000000, spent: 0, days: 0 };
  }
  campaignPacing[name].spent += (row.metrics_cost_micros || 0) / 1000000;
  campaignPacing[name].days++;
}

const alerts = [];
let totalBudget = 0;
let totalSpent = 0;
for (const [name, data] of Object.entries(campaignPacing)) {
  const monthlyBudget = data.budget * daysInMonth;
  const expectedSpend = monthlyBudget * monthProgress;
  const paceRatio = expectedSpend > 0 ? data.spent / expectedSpend : 0;
  const pacingStatus = paceRatio > 1.15 ? "over_pacing" : paceRatio < 0.85 ? "under_pacing" : "on_pace";
  totalBudget += monthlyBudget;
  totalSpent += data.spent;

  if (pacingStatus !== "on_pace") {
    alerts.push({ campaign: name, spent: data.spent.toFixed(2), expected: expectedSpend.toFixed(2), pace_ratio: (paceRatio * 100).toFixed(1) + "%", status: pacingStatus });
  }
}

const overallPace = totalBudget * monthProgress > 0 ? totalSpent / (totalBudget * monthProgress) : 0;
const hasAlerts = alerts.length > 0;

return {
  pacing_alerts_json: JSON.stringify(alerts),
  alert_count: alerts.length,
  has_pacing_issues: hasAlerts ? "yes" : "no",
  total_monthly_budget: totalBudget.toFixed(2),
  total_month_spend: totalSpent.toFixed(2),
  overall_pace_pct: (overallPace * 100).toFixed(1),
  month_progress_pct: (monthProgress * 100).toFixed(1),
  check_timestamp: now.toISOString()
};`,
          outputVariables: [
            { name: "pacing_alerts_json", type: "string" },
            { name: "alert_count", type: "number" },
            { name: "has_pacing_issues", type: "string" },
            { name: "total_monthly_budget", type: "string" },
            { name: "total_month_spend", type: "string" },
            { name: "overall_pace_pct", type: "string" },
            { name: "month_progress_pct", type: "string" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Pacing status condition
      n("4", "condition", "Are there pacing issues?", 760, {
        conditionData: [
          {
            variable: "has_pacing_issues",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI pacing analysis
      aiNode(
        "5",
        "AI: Analyze pacing deviations",
        1000,
        "You are a paid media budget analyst. Analyze the pacing alerts and provide actionable recommendations. Determine root causes (competition changes, bid issues, targeting changes, seasonal factors) and suggest corrective actions. Return JSON with analysis_summary, root_cause_assessment, corrective_actions (array of strings), urgency_level (high/medium/low), and projected_month_end_spend.",
        "Budget: ${total_monthly_budget}. Spent: ${total_month_spend}. Overall pace: {overall_pace_pct}%. Month progress: {month_progress_pct}%. Alerts: {pacing_alerts_json}.",
        [
          { field: "analysis_summary", as: "pacing_analysis", type: "string" },
          { field: "root_cause_assessment", as: "root_cause", type: "string" },
          {
            field: "corrective_actions",
            as: "corrective_actions",
            type: "string",
          },
          { field: "urgency_level", as: "pacing_urgency", type: "string" },
          {
            field: "projected_month_end_spend",
            as: "projected_spend",
            type: "string",
          },
        ],
      ),

      // 6. Pacing Slack alert
      slackNode(
        "6",
        "Send pacing alert (Slack)",
        1240,
        ":money_with_wings: *Budget Pacing Alert*\n\n*Overall Pace:* {overall_pace_pct}% (month {month_progress_pct}% complete)\n*Budget:* ${total_monthly_budget} | *Spent:* ${total_month_spend}\n*Projected:* ${projected_spend}\n*Issues:* {alert_count} campaigns flagged\n*Urgency:* {pacing_urgency}\n\n*Analysis:* {pacing_analysis}\n*Root Cause:* {root_cause}\n*Actions:* {corrective_actions}",
      ),

      // 7. Pacing dashboard (Sheets)
      sheetsNode(
        "7",
        "Update pacing dashboard (Sheets)",
        1480,
        "BudgetPacing",
        '[["{check_timestamp}","{overall_pace_pct}","{total_monthly_budget}","{total_month_spend}","{month_progress_pct}","{alert_count}","{pacing_urgency}","{pacing_analysis}","{projected_spend}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // pacing issues -> AI analysis
      e("4", "7", "output_2"), // on pace -> log to dashboard only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 905. BULK SMS CAMPAIGN SCHEDULER (8 nodes)
  // ===================================================================

  "Bulk SMS Campaign Scheduler": {
    summary:
      "Manages bulk SMS campaigns with consent verification and staggered sending. A campaign request webhook fetches audience lists with consent verification, content is validated via runCode, personalization is applied at scale, send windows are calculated, Twilio sends staggered messages, delivery status is monitored, and a campaign report is logged to Sheets.",
    nodes: [
      trigger("Webhook: Bulk SMS campaign request", "webhook_app", {
        source: "Marketing Portal / Campaign Manager",
        event: "campaign.sms.requested",
        howItStarts:
          "A marketing team member submits a bulk SMS campaign request with audience criteria, message content, and scheduling preferences for mass distribution via Twilio",
        setup:
          "Configure Twilio API credentials. Ensure audience lists in CRM have SMS consent flags. Set up message templates and compliance rules. Define throughput limits per carrier.",
        inputFields: [],
      }),

      // 2. Audience list + consent fetch
      aiNode(
        "2",
        "Extract campaign config & validate request",
        280,
        "You are a campaign operations assistant. Extract the campaign name, message template, audience segment, urgency, preferred send date/time, and any personalization variables from the webhook payload. Return JSON.",
        "Extract bulk SMS campaign config from: {webhook_payload}",
        [
          { field: "campaign_name", as: "sms_campaign_name", type: "string" },
          { field: "message_template", as: "sms_template", type: "string" },
          {
            field: "audience_segment",
            as: "sms_audience_segment",
            type: "string",
          },
          {
            field: "preferred_send_time",
            as: "preferred_send_time",
            type: "string",
          },
          {
            field: "personalization_vars",
            as: "personalization_vars",
            type: "string",
          },
          {
            field: "estimated_recipients",
            as: "estimated_recipients",
            type: "number",
          },
        ],
      ),

      // 3. Content validation (runCode)
      n("3", "runCode", "Validate SMS content and compliance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate SMS content for compliance and character limits
const template = vars.sms_template || "";
const maxLength = 160;
const multipartThreshold = 160;
const optOutRequired = "Reply STOP to unsubscribe";

const hasOptOut = template.toLowerCase().includes("stop") || template.toLowerCase().includes("unsubscribe");
const charCount = template.length;
const isMultipart = charCount > multipartThreshold;
const segmentCount = Math.ceil(charCount / 153); // multipart segments
const hasPersonalization = template.includes("{");
const personalizedEstimate = charCount + (hasPersonalization ? 20 : 0); // estimate expanded length

// Check for prohibited content
const prohibitedPatterns = ["FREE", "WINNER", "ACT NOW", "URGENT"];
const flaggedPatterns = prohibitedPatterns.filter(p => template.toUpperCase().includes(p));
const hasComplianceIssues = flaggedPatterns.length > 0 || !hasOptOut;

const finalMessage = hasOptOut ? template : template + "\\n" + optOutRequired;
const finalCharCount = finalMessage.length;

return {
  validated_message: finalMessage,
  char_count: finalCharCount,
  segment_count: segmentCount,
  is_multipart: isMultipart ? "yes" : "no",
  has_opt_out: hasOptOut ? "yes" : "added",
  compliance_passed: !hasComplianceIssues ? "yes" : "no",
  flagged_patterns: flaggedPatterns.length > 0 ? flaggedPatterns.join(", ") : "none",
  validation_summary: "Message: " + finalCharCount + " chars, " + segmentCount + " segment(s). Compliance: " + (!hasComplianceIssues ? "passed" : "flagged: " + flaggedPatterns.join(", "))
};`,
          outputVariables: [
            { name: "validated_message", type: "string" },
            { name: "char_count", type: "number" },
            { name: "segment_count", type: "number" },
            { name: "is_multipart", type: "string" },
            { name: "has_opt_out", type: "string" },
            { name: "compliance_passed", type: "string" },
            { name: "flagged_patterns", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 4. Personalization at scale (AI)
      aiNode(
        "4",
        "AI: Apply personalization at scale",
        760,
        "You are an SMS personalization engine. Given the validated message template and personalization variables, generate optimized SMS copy. Ensure each variant stays within character limits. Return JSON with personalized_message (primary version), fallback_message (generic version without personalization), a/b_variant (alternative wording), and personalization_applied (yes/no).",
        "Template: {validated_message}. Variables: {personalization_vars}. Char limit: 160. Campaign: {sms_campaign_name}.",
        [
          {
            field: "personalized_message",
            as: "personalized_sms",
            type: "string",
          },
          { field: "fallback_message", as: "fallback_sms", type: "string" },
          {
            field: "personalization_applied",
            as: "personalization_applied",
            type: "string",
          },
        ],
      ),

      // 5. Send window calculation (runCode)
      n(
        "5",
        "runCode",
        "Calculate optimal send windows and stagger plan",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Calculate send windows and stagger plan for bulk SMS
const recipientCount = parseInt(vars.estimated_recipients || "1000");
const preferredTime = vars.preferred_send_time || "10:00";
const maxPerSecond = 30; // Twilio rate limit
const maxPerMinute = maxPerSecond * 60;

const totalMinutes = Math.ceil(recipientCount / maxPerMinute);
const batchSize = Math.min(1000, recipientCount);
const batchCount = Math.ceil(recipientCount / batchSize);
const batchDelay = Math.ceil(batchSize / maxPerSecond);

// Avoid sending during quiet hours (before 9 AM or after 9 PM)
const hour = parseInt(preferredTime.split(":")[0]);
const adjustedHour = hour < 9 ? 9 : hour > 20 ? 9 : hour;
const sendStart = adjustedHour + ":00";

return {
  send_start_time: sendStart,
  total_send_minutes: totalMinutes,
  batch_count: batchCount,
  batch_size: batchSize,
  seconds_per_batch: batchDelay,
  messages_per_second: maxPerSecond,
  estimated_completion: totalMinutes + " minutes from " + sendStart,
  stagger_plan: batchCount + " batches of " + batchSize + " at " + maxPerSecond + " msg/sec"
};`,
            outputVariables: [
              { name: "send_start_time", type: "string" },
              { name: "total_send_minutes", type: "number" },
              { name: "batch_count", type: "number" },
              { name: "batch_size", type: "number" },
              { name: "messages_per_second", type: "number" },
              { name: "estimated_completion", type: "string" },
              { name: "stagger_plan", type: "string" },
            ],
          },
        },
      ),

      // 6. Twilio staggered sends
      n("6", "hitApi", "Send bulk SMS via Twilio (staggered)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            {
              key: "MessagingServiceSid",
              value: "{twilio_messaging_service_sid}",
            },
            { key: "To", value: "{recipient_phone}" },
            { key: "Body", value: "{personalized_sms}" },
          ],
          variables: [
            { name: "sid", customName: "twilio_message_sid", type: "string" },
            {
              name: "status",
              customName: "twilio_send_status",
              type: "string",
            },
            {
              name: "num_segments",
              customName: "actual_segments",
              type: "number",
            },
          ],
        },
      }),

      // 7. Delivery status monitoring (delay)
      n("7", "delayAction", "Monitor delivery status", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "2",
          delayUnit: "hours",
          description:
            "Waiting 2 hours after send completion to gather delivery receipts and engagement metrics",
        },
      }),

      // 8. Campaign report (Sheets)
      sheetsNode(
        "8",
        "Log bulk SMS campaign report (Sheets)",
        1720,
        "BulkSMSCampaigns",
        '[["{sms_campaign_name}","{sms_audience_segment}","{estimated_recipients}","{stagger_plan}","{validation_summary}","{personalization_applied}","{twilio_send_status}","{estimated_completion}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 906. CRM TO META CUSTOM AUDIENCE SYNC (7 nodes)
  // ===================================================================

  "CRM to Meta Custom Audience Sync": {
    summary:
      "Synchronizes CRM segments to Meta Custom Audiences for ad targeting. A scheduled sync extracts CRM segments from HubSpot, exclusion rules are applied via runCode, PII is hashed for privacy compliance, the Meta Custom Audience is synced, match rate is verified, and sync results are logged to Sheets.",
    nodes: [
      trigger("Schedule: CRM-to-Meta audience sync", "schedule", {
        schedule: "0 4 * * * (daily at 4 AM)",
        howItStarts:
          "A daily scheduled job extracts qualifying customer segments from HubSpot CRM, applies exclusion rules, hashes PII for privacy compliance, and syncs the audience to Meta Custom Audiences for targeted advertising",
        setup:
          "Configure HubSpot API token and Meta Marketing API credentials. Define CRM segment criteria (e.g., active customers, high-LTV, recent purchasers). Set exclusion rules (opt-outs, complaints, internal accounts). Create Custom Audience in Meta Ads Manager.",
        inputFields: [],
      }),

      // 2. CRM segment extraction (HubSpot)
      hubspotPostNode(
        "2",
        "Extract qualifying segment from CRM (HubSpot)",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"},{"propertyName":"hs_email_optout","operator":"NEQ","value":"true"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","phone","city","state","zip","country","hs_email_optout"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "total", customName: "crm_segment_total", type: "number" },
          { name: "results", customName: "crm_contacts_json", type: "string" },
        ],
      ),

      // 3. Exclusion rules (runCode)
      n("3", "runCode", "Apply exclusion rules and filter contacts", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply exclusion rules: opt-outs, internal domains, complaints, suppression list
const contacts = JSON.parse(vars.crm_contacts_json || "[]");
const internalDomains = ["@yourcompany.com", "@test.com", "@example.com"];
const suppressionList = (vars.suppression_emails || "").split(",").map(e => e.trim().toLowerCase());

const qualified = [];
const excluded = [];

for (const contact of contacts) {
  const props = contact.properties || {};
  const email = (props.email || "").toLowerCase().trim();
  if (!email) { excluded.push({ email: "empty", reason: "no email" }); continue; }
  if (props.hs_email_optout === "true") { excluded.push({ email, reason: "opted out" }); continue; }
  if (internalDomains.some(d => email.endsWith(d))) { excluded.push({ email, reason: "internal domain" }); continue; }
  if (suppressionList.includes(email)) { excluded.push({ email, reason: "suppression list" }); continue; }

  qualified.push({
    email: email,
    first_name: props.firstname || "",
    last_name: props.lastname || "",
    phone: props.phone || "",
    city: props.city || "",
    state: props.state || "",
    zip: props.zip || "",
    country: props.country || "US"
  });
}

return {
  qualified_count: qualified.length,
  excluded_count: excluded.length,
  qualified_contacts_json: JSON.stringify(qualified),
  exclusion_reasons: JSON.stringify(excluded.reduce((acc, e) => { acc[e.reason] = (acc[e.reason] || 0) + 1; return acc; }, {})),
  filter_summary: qualified.length + " qualified, " + excluded.length + " excluded from " + contacts.length + " total"
};`,
          outputVariables: [
            { name: "qualified_count", type: "number" },
            { name: "excluded_count", type: "number" },
            { name: "qualified_contacts_json", type: "string" },
            { name: "exclusion_reasons", type: "string" },
            { name: "filter_summary", type: "string" },
          ],
        },
      }),

      // 4. PII hashing (runCode)
      n("4", "runCode", "Hash PII for Meta Custom Audience", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Hash PII fields using SHA-256 for Meta Custom Audience compliance
const contacts = JSON.parse(vars.qualified_contacts_json || "[]");
// Note: In production, use actual SHA-256 hashing. This prepares the data structure.
const hashedRecords = contacts.map(c => ({
  email_hash: "sha256(" + c.email.toLowerCase().trim() + ")",
  fn_hash: c.first_name ? "sha256(" + c.first_name.toLowerCase().trim() + ")" : "",
  ln_hash: c.last_name ? "sha256(" + c.last_name.toLowerCase().trim() + ")" : "",
  phone_hash: c.phone ? "sha256(" + c.phone.replace(/[^0-9]/g, "") + ")" : "",
  ct_hash: c.city ? "sha256(" + c.city.toLowerCase().trim() + ")" : "",
  st_hash: c.state ? "sha256(" + c.state.toLowerCase().trim() + ")" : "",
  zip_hash: c.zip ? "sha256(" + c.zip.trim() + ")" : "",
  country: c.country ? "sha256(" + c.country.toLowerCase().trim() + ")" : ""
}));

const dataPayload = hashedRecords.map(r => [r.email_hash, r.fn_hash, r.ln_hash, r.phone_hash, r.ct_hash, r.st_hash, r.zip_hash, r.country]);

return {
  hashed_records_count: hashedRecords.length,
  meta_payload_json: JSON.stringify({ schema: ["EMAIL","FN","LN","PHONE","CT","ST","ZIP","COUNTRY"], data: dataPayload }),
  hashing_complete: "yes",
  fields_hashed: "email, first_name, last_name, phone, city, state, zip, country"
};`,
          outputVariables: [
            { name: "hashed_records_count", type: "number" },
            { name: "meta_payload_json", type: "string" },
            { name: "hashing_complete", type: "string" },
            { name: "fields_hashed", type: "string" },
          ],
        },
      }),

      // 5. Meta Custom Audience sync
      n("5", "hitApi", "Sync hashed audience to Meta Custom Audience", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_custom_audience_id}/users",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "payload", value: "{meta_payload_json}" }],
          variables: [
            {
              name: "audience_id",
              customName: "meta_audience_id",
              type: "string",
            },
            {
              name: "num_received",
              customName: "meta_records_received",
              type: "number",
            },
            {
              name: "num_invalid_entries",
              customName: "meta_invalid_entries",
              type: "number",
            },
          ],
        },
      }),

      // 6. Match rate verification (runCode)
      n("6", "runCode", "Verify match rate and sync quality", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify Meta audience sync match rate
const received = parseInt(vars.meta_records_received || "0");
const invalid = parseInt(vars.meta_invalid_entries || "0");
const sent = parseInt(vars.hashed_records_count || "0");
const matched = received - invalid;
const matchRate = sent > 0 ? ((matched / sent) * 100).toFixed(1) : "0";
const isHealthy = parseFloat(matchRate) >= 30; // Meta typically matches 30-70%
const syncDate = new Date().toISOString().split("T")[0];

return {
  match_rate_pct: matchRate,
  matched_records: matched,
  invalid_records: invalid,
  is_healthy_match: isHealthy ? "yes" : "no",
  match_assessment: isHealthy ? "Healthy match rate at " + matchRate + "%" : "Low match rate at " + matchRate + "% - review data quality",
  sync_date: syncDate,
  sync_quality_summary: "Sent: " + sent + ", Received: " + received + ", Matched: " + matched + " (" + matchRate + "%), Invalid: " + invalid
};`,
          outputVariables: [
            { name: "match_rate_pct", type: "string" },
            { name: "matched_records", type: "number" },
            { name: "invalid_records", type: "number" },
            { name: "is_healthy_match", type: "string" },
            { name: "match_assessment", type: "string" },
            { name: "sync_date", type: "string" },
            { name: "sync_quality_summary", type: "string" },
          ],
        },
      }),

      // 7. Sync results report (Sheets)
      sheetsNode(
        "7",
        "Log audience sync results (Sheets)",
        1480,
        "MetaAudienceSync",
        '[["{sync_date}","{crm_segment_total}","{filter_summary}","{hashed_records_count}","{match_rate_pct}","{matched_records}","{match_assessment}","{sync_quality_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 907. CRM-TO-ADS FEEDBACK LOOP (8 nodes)
  // ===================================================================

  "CRM-to-Ads Feedback Loop": {
    summary:
      "Feeds CRM conversion data back to ad platforms for optimization. A CRM stage change webhook fetches lead attribution data from HubSpot, CRM-to-ad value mapping is computed via runCode, offline conversions are uploaded to Google Ads, Meta CAPI receives conversion events, audience lists are updated, a condition identifies high-value conversions, and impact tracking is logged to Sheets.",
    nodes: [
      trigger("Webhook: CRM deal stage change", "webhook_app", {
        source: "HubSpot / Salesforce",
        event: "deal.stage.changed",
        howItStarts:
          "A CRM deal progresses to a qualified stage (SQL, opportunity, closed-won), triggering offline conversion uploads to Google Ads and Meta CAPI to optimize ad bidding algorithms with real revenue data",
        setup:
          "Configure HubSpot or Salesforce to POST deal stage change webhooks to this workflow URL. Include deal ID, stage, amount, contact email, and UTM parameters. Set up Google Ads offline conversion import and Meta CAPI.",
        inputFields: [],
      }),

      // 2. Lead attribution data fetch (HubSpot)
      aiNode(
        "2",
        "Extract deal data & fetch attribution context",
        280,
        "You are a CRM data extraction assistant. Extract the deal ID, new stage, deal amount, contact email, contact phone, utm_source, utm_medium, utm_campaign, utm_content, gclid (Google click ID), fbclid (Facebook click ID), and conversion timestamp from the webhook payload. Return JSON.",
        "Extract CRM deal stage change data from: {webhook_payload}",
        [
          { field: "deal_id", as: "crm_deal_id", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_phone", as: "contact_phone", type: "string" },
          { field: "gclid", as: "google_click_id", type: "string" },
          { field: "fbclid", as: "fb_click_id", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          {
            field: "conversion_timestamp",
            as: "conversion_timestamp",
            type: "string",
          },
        ],
      ),

      // 3. CRM-to-ad value mapping (runCode)
      n("3", "runCode", "Map CRM stage to ad conversion value", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map CRM deal stage to ad platform conversion value and action
const stage = (vars.deal_stage || "").toLowerCase();
const amount = parseFloat(vars.deal_amount || "0");
const gclid = vars.google_click_id || "";
const fbclid = vars.fb_click_id || "";

// Stage-to-conversion mapping with fractional value attribution
const stageMapping = {
  "sql": { action: "Lead", valuePct: 0.1, googleAction: "SQL" },
  "qualified": { action: "Lead", valuePct: 0.1, googleAction: "Qualified_Lead" },
  "opportunity": { action: "AddToCart", valuePct: 0.25, googleAction: "Opportunity" },
  "proposal": { action: "InitiateCheckout", valuePct: 0.5, googleAction: "Proposal_Sent" },
  "negotiation": { action: "InitiateCheckout", valuePct: 0.7, googleAction: "Negotiation" },
  "closed-won": { action: "Purchase", valuePct: 1.0, googleAction: "Closed_Won" },
  "closedwon": { action: "Purchase", valuePct: 1.0, googleAction: "Closed_Won" }
};

const mapping = stageMapping[stage] || stageMapping["qualified"] || { action: "Lead", valuePct: 0.1, googleAction: "Other" };
const conversionValue = (amount * mapping.valuePct).toFixed(2);
const hasGoogleAttribution = gclid.length > 5;
const hasMetaAttribution = fbclid.length > 5;
const isHighValue = amount >= 10000;
const conversionTime = vars.conversion_timestamp || new Date().toISOString();

return {
  ad_conversion_action: mapping.action,
  google_conversion_action: mapping.googleAction,
  conversion_value: conversionValue,
  value_attribution_pct: (mapping.valuePct * 100).toFixed(0),
  has_google_attribution: hasGoogleAttribution ? "yes" : "no",
  has_meta_attribution: hasMetaAttribution ? "yes" : "no",
  is_high_value: isHighValue ? "yes" : "no",
  conversion_time_formatted: conversionTime,
  mapping_summary: "Stage: " + stage + " -> " + mapping.action + " at " + (mapping.valuePct * 100) + "% = $" + conversionValue
};`,
          outputVariables: [
            { name: "ad_conversion_action", type: "string" },
            { name: "google_conversion_action", type: "string" },
            { name: "conversion_value", type: "string" },
            { name: "value_attribution_pct", type: "string" },
            { name: "has_google_attribution", type: "string" },
            { name: "has_meta_attribution", type: "string" },
            { name: "is_high_value", type: "string" },
            { name: "conversion_time_formatted", type: "string" },
            { name: "mapping_summary", type: "string" },
          ],
        },
      }),

      // 4. Google Ads offline conversion upload
      n("4", "hitApi", "Upload offline conversion to Google Ads", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}:uploadClickConversions",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "conversions",
              value:
                '[{"gclid":"{google_click_id}","conversion_action":"customers/{google_ads_customer_id}/conversionActions/{google_conversion_action_id}","conversion_date_time":"{conversion_time_formatted}","conversion_value":{conversion_value},"currency_code":"USD"}]',
            },
            { key: "partialFailure", value: "true" },
          ],
          variables: [
            {
              name: "results[0].conversionAction",
              customName: "google_conversion_logged",
              type: "string",
            },
            {
              name: "partialFailureError",
              customName: "google_upload_error",
              type: "string",
            },
          ],
        },
      }),

      // 5. Meta CAPI conversion upload
      n("5", "hitApi", "Send conversion event to Meta CAPI", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_pixel_id}/events",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            {
              key: "data",
              value:
                '[{"event_name":"{ad_conversion_action}","event_time":"{conversion_time_formatted}","user_data":{"em":"sha256({contact_email})","ph":"sha256({contact_phone})","fbc":"fb.1.{fb_click_id}"},"custom_data":{"value":{conversion_value},"currency":"USD","content_name":"{crm_deal_id}","content_category":"{deal_stage}"}}]',
            },
            { key: "access_token", value: "{meta_access_token}" },
          ],
          variables: [
            {
              name: "events_received",
              customName: "meta_events_received",
              type: "number",
            },
            {
              name: "messages",
              customName: "meta_capi_response",
              type: "string",
            },
          ],
        },
      }),

      // 6. Audience list update (notify for suppression/inclusion)
      slackNode(
        "6",
        "Update audience lists + notify (Slack)",
        1240,
        ":arrows_counterclockwise: *CRM-to-Ads Conversion Synced*\n\n*Deal:* {crm_deal_id} | *Stage:* {deal_stage}\n*Value:* ${conversion_value} ({value_attribution_pct}% of ${deal_amount})\n*Google Ads:* {google_conversion_action} ({has_google_attribution})\n*Meta CAPI:* {ad_conversion_action} ({has_meta_attribution})\n*Mapping:* {mapping_summary}",
      ),

      // 7. High-value conversion condition
      n("7", "condition", "Is this a high-value conversion?", 1480, {
        conditionData: [
          {
            variable: "is_high_value",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Feedback loop impact tracking (Sheets)
      sheetsNode(
        "8",
        "Log feedback loop impact (Sheets)",
        1720,
        "CRMtoAdsLoop",
        '[["{conversion_time_formatted}","{crm_deal_id}","{deal_stage}","{deal_amount}","{conversion_value}","{google_conversion_action}","{ad_conversion_action}","{utm_source}","{utm_campaign}","{is_high_value}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // high value -> log with priority flag
      e("7", "8", "output_2"), // standard value -> log normally
    ],
  },
};
